import React, { useState, useEffect, useMemo } from "react";
import {
  CreditCard,
  RotateCcw,
  CheckCircle2,
  AlertTriangle,
  Search,
  RefreshCw,
  Download,
  Copy,
  Check,
  Eye,
  PlusCircle,
  X,
  ShieldAlert,
  Calendar,
  MapPin,
  Phone,
  Filter,
  Package,
  FileText,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  "https://api.vanyacollectionsrajahmundry.com";

// ==========================================================
// HELPER: PARSE ORDER ITEMS INTO CLEAN HUMAN-READABLE OBJECTS
// ==========================================================
const parseInspectorItems = (item) => {
  if (!item) return [];
  const list = [];
  let rawItems = item.order?.items;

  if (typeof rawItems === "string") {
    try {
      rawItems = JSON.parse(rawItems);
    } catch (e) {
      rawItems = [];
    }
  }

  if (Array.isArray(rawItems) && rawItems.length > 0) {
    rawItems.forEach((it) => {
      list.push({
        name: it.name || it.title || "Saree Product",
        productId:
          it.product_id ||
          it.productId ||
          (typeof it.id === "number" ? it.id : null),
        colour:
          it.colour ||
          it.color ||
          it.variant?.colour ||
          it.variant?.color ||
          null,
        size: it.size || it.variant?.size || null,
        quantity: Number(it.quantity || it.qty || 1),
        price: Number(it.price || it.variant?.price || 0),
        image:
          it.img ||
          it.image ||
          it.img_url ||
          it.variant?.img_url ||
          it.variant?.mainImage ||
          it.mainImage ||
          null,
      });
    });
    return list;
  }

  // Fallback to Razorpay notes (e.g. item_1, item_2...)
  const notes = item.notes || {};
  Object.keys(notes).forEach((k) => {
    if (k.startsWith("item_")) {
      const val = String(notes[k]);
      const idMatch = val.match(/\[ID:([^\]]+)\]/);
      const qtyMatch = val.match(/Qty:(\d+)/);
      const priceMatch = val.match(/Rs\.([\d.]+)/);
      const colMatch = val.match(/Col:([^,)]+)/);
      const sizeMatch = val.match(/Size:([^,)]+)/);
      const cleanName = val
        .replace(/\[ID:[^\]]+\]\s*/, "")
        .split("|")[0]
        .replace(/\([^)]*\)/, "")
        .trim();
      list.push({
        name: cleanName || val,
        productId: idMatch ? idMatch[1] : null,
        colour: colMatch ? colMatch[1].trim() : null,
        size: sizeMatch ? sizeMatch[1].trim() : null,
        quantity: qtyMatch ? Number(qtyMatch[1]) : 1,
        price: priceMatch ? Number(priceMatch[1]) : null,
        image: null,
      });
    }
  });

  return list;
};

export default function RazorpayCollections() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState({
    totalCapturedCount: 0,
    totalCapturedAmount: 0,
    withOrdersCount: 0,
    withOrdersAmount: 0,
    withoutOrdersCount: 0,
    withoutOrdersAmount: 0,
    totalRefundedCount: 0,
    totalRefundedAmount: 0,
    databaseOrdersCount: 0,
    lastUpdated: null,
  });
  const [paymentsWithOrders, setPaymentsWithOrders] = useState([]);
  const [paymentsWithoutOrders, setPaymentsWithoutOrders] = useState([]);

  // Active Tab: 'without_orders' (missing in DB) | 'with_orders' (connected)
  const [activeTab, setActiveTab] = useState("without_orders");

  // Filters & Search
  const [searchQuery, setSearchQuery] = useState("");
  const [methodFilter, setMethodFilter] = useState("all");
  const [refundFilter, setRefundFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all"); // 'all', 'today', 'yesterday', 'week', 'month'

  // Clipboard tracking
  const [copiedId, setCopiedId] = useState(null);

  // Inspector modal (shows full payment & notes details)
  const [inspectModal, setInspectModal] = useState({
    open: false,
    item: null,
  });
  const [showRawJson, setShowRawJson] = useState(false);

  // TOAST CONFIRMATION NOTIFICATION (HIGH PROMINENCE AT TOP)
  const [toastNotification, setToastNotification] = useState(null);

  useEffect(() => {
    if (!toastNotification) return;
    const timer = setTimeout(() => {
      setToastNotification(null);
    }, 10000);
    return () => clearTimeout(timer);
  }, [toastNotification]);

  // HIGH PROMPT DIRECT REFUND MODAL
  const [refundModal, setRefundModal] = useState({
    open: false,
    payment: null,
    refundAmount: "",
    refundReason: "Payment captured without order in database (Reconciliation Refund)",
    confirmText: "",
    processing: false,
    errorMessage: "",
    successMessage: "",
    successData: null, // { refundId, amount, paymentId, customer }
  });

  // CREATE ORDER FROM PAYMENT MODAL
  const [createOrderModal, setCreateOrderModal] = useState({
    open: false,
    payment: null,
    fullName: "",
    phone: "",
    altPhone: "",
    email: "",
    address: "",
    city: "",
    state: "",
    pinCode: "",
    amount: "",
    itemName: "",
    quantity: 1,
    processing: false,
    errorMessage: "",
    successMessage: "",
    successData: null, // { orderNumber, paymentId }
  });

  // Explanation banner collapsed state
  const [showExplanation, setShowExplanation] = useState(true);

  // ==========================================================
  // FETCH COLLECTIONS DATA (CACHE-BUSTED TO ALWAYS GET LIVE STATUS)
  // ==========================================================
  const fetchCollections = async (isManualRefresh = false) => {
    if (isManualRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      const url = `${API_BASE_URL}/orders/razorpay/collections?force=true&_t=${Date.now()}`;
      const res = await fetch(url, {
        cache: "no-store",
        headers: {
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
        },
      });
      const data = await res.json();

      if (res.ok && data.success) {
        setStats(data.stats || {});
        setPaymentsWithOrders(data.paymentsWithOrders || []);
        setPaymentsWithoutOrders(data.paymentsWithoutOrders || []);
      } else {
        console.warn("Collections fetch note:", data.message);
      }
    } catch (err) {
      console.error("Fetch collections error:", err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchCollections();
  }, []);

  // Copy to clipboard helper
  const handleCopy = (text, id) => {
    if (!text) return;
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(text);
      setCopiedId(id || text);
      setTimeout(() => setCopiedId(null), 2000);
    }
  };

  // Date formatting helpers
  const formatDateTime = (isoStr) => {
    if (!isoStr) return "-";
    const d = new Date(isoStr);
    if (isNaN(d.getTime())) return "-";
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    let hours = d.getHours();
    const minutes = String(d.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    return `${day}-${month}-${year} at ${String(hours).padStart(2, "0")}:${minutes} ${ampm}`;
  };

  // ==========================================================
  // FILTERING LOGIC
  // ==========================================================
  const currentList =
    activeTab === "without_orders" ? paymentsWithoutOrders : paymentsWithOrders;

  const filteredList = useMemo(() => {
    return currentList.filter((item) => {
      // Search
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const pid = (item.id || "").toLowerCase();
        const oid = (item.order_id || "").toLowerCase();
        const name = (
          item.customer_name ||
          item.order?.full_name ||
          item.notes?.customer_name ||
          ""
        ).toLowerCase();
        const contact = (
          item.contact ||
          item.customer_phone ||
          item.order?.phone ||
          ""
        ).toLowerCase();
        const email = (
          item.email ||
          item.order?.email ||
          ""
        ).toLowerCase();
        const amt = String(item.amount || item.order?.total_amount || "");
        const orderNum = (item.order?.order_number || "").toLowerCase();

        const matchSearch =
          pid.includes(q) ||
          oid.includes(q) ||
          name.includes(q) ||
          contact.includes(q) ||
          email.includes(q) ||
          amt.includes(q) ||
          orderNum.includes(q);

        if (!matchSearch) return false;
      }

      // Method filter
      if (methodFilter !== "all") {
        if ((item.method || "").toLowerCase() !== methodFilter.toLowerCase()) {
          return false;
        }
      }

      // Refund status filter
      if (refundFilter !== "all") {
        const isRefunded =
          item.refund_status ||
          (item.amount_refunded && Number(item.amount_refunded) > 0) ||
          item.status === "refunded";
        if (refundFilter === "refunded" && !isRefunded) return false;
        if (refundFilter === "not_refunded" && isRefunded) return false;
      }

      // Date preset filter
      if (dateFilter !== "all") {
        const itemDate = new Date(item.created_at);
        const now = new Date();
        const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());

        if (dateFilter === "today") {
          if (itemDate < startOfToday) return false;
        } else if (dateFilter === "yesterday") {
          const startOfYesterday = new Date(startOfToday);
          startOfYesterday.setDate(startOfYesterday.getDate() - 1);
          if (itemDate < startOfYesterday || itemDate >= startOfToday) return false;
        } else if (dateFilter === "week") {
          const weekAgo = new Date(now);
          weekAgo.setDate(weekAgo.getDate() - 7);
          if (itemDate < weekAgo) return false;
        } else if (dateFilter === "month") {
          const monthAgo = new Date(now);
          monthAgo.setDate(monthAgo.getDate() - 30);
          if (itemDate < monthAgo) return false;
        }
      }

      return true;
    });
  }, [currentList, searchQuery, methodFilter, refundFilter, dateFilter]);

  // ==========================================================
  // OPEN HIGH PROMPT REFUND MODAL
  // ==========================================================
  const handleOpenRefundModal = (item) => {
    const refundable =
      item.amount - (Number(item.amount_refunded) || 0);

    setRefundModal({
      open: true,
      payment: item,
      refundAmount: String(refundable > 0 ? refundable : item.amount),
      refundReason: "Payment captured without order in database (Reconciliation Refund)",
      confirmText: "",
      processing: false,
      errorMessage: "",
      successMessage: "",
      successData: null,
    });
  };

  // EXECUTE REFUND
  const handleExecuteRefund = async () => {
    if (!refundModal.payment) return;
    const cleanConfirm = (refundModal.confirmText || "").trim().toUpperCase();

    if (cleanConfirm !== "REFUND") {
      setRefundModal((prev) => ({
        ...prev,
        errorMessage: 'Please type "REFUND" in capital letters to confirm this action.',
      }));
      return;
    }

    const numAmount = Number(refundModal.refundAmount);
    if (!numAmount || numAmount <= 0) {
      setRefundModal((prev) => ({
        ...prev,
        errorMessage: "Please enter a valid positive refund amount.",
      }));
      return;
    }

    setRefundModal((prev) => ({
      ...prev,
      processing: true,
      errorMessage: "",
      successMessage: "",
      successData: null,
    }));

    try {
      const res = await fetch(`${API_BASE_URL}/orders/razorpay/refund`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          payment_id: refundModal.payment.id,
          amount: numAmount,
          reason: refundModal.refundReason,
        }),
      });

      const data = await res.json();

      if (res.ok && data.success) {
        const targetPaymentId = refundModal.payment.id;
        const refundId = data.refund_id || data.refund?.id || "Completed";

        // 1. INSTANT OPTIMISTIC UPDATE: paymentsWithoutOrders
        setPaymentsWithoutOrders((prev) =>
          prev.map((item) =>
            item.id === targetPaymentId
              ? {
                  ...item,
                  status: "refunded",
                  refund_status: "full",
                  amount_refunded: numAmount,
                }
              : item
          )
        );

        // 2. INSTANT OPTIMISTIC UPDATE: paymentsWithOrders
        setPaymentsWithOrders((prev) =>
          prev.map((item) =>
            item.id === targetPaymentId
              ? {
                  ...item,
                  status: "refunded",
                  refund_status: "full",
                  amount_refunded: numAmount,
                }
              : item
          )
        );

        // 3. INSTANT OPTIMISTIC STATS UPDATE
        setStats((prev) => ({
          ...prev,
          totalRefundedCount: (prev.totalRefundedCount || 0) + 1,
          totalRefundedAmount: (prev.totalRefundedAmount || 0) + numAmount,
        }));

        // 4. DISPLAY PROMINENT TOP-SCREEN TOAST NOTIFICATION
        setToastNotification({
          type: "success",
          title: "✓ Refund Processed Successfully!",
          message: `₹${numAmount.toLocaleString("en-IN")} was refunded back to customer via Razorpay.`,
          refundId,
          paymentId: targetPaymentId,
        });

        // 5. UPDATE MODAL WITH PERMANENT SUCCESS RECEIPT
        setRefundModal((prev) => ({
          ...prev,
          processing: false,
          errorMessage: "",
          successMessage: `✓ Success! Refund of ₹${numAmount.toLocaleString("en-IN")} processed directly via Razorpay.`,
          successData: {
            refundId,
            amount: numAmount,
            paymentId: targetPaymentId,
            customer: prev.payment.customer_name || prev.payment.contact || "Customer",
          },
        }));

        // 6. Quiet background sync after 2.5s
        setTimeout(() => {
          fetchCollections(false);
        }, 2500);
      } else {
        setRefundModal((prev) => ({
          ...prev,
          processing: false,
          errorMessage:
            data.message || "Razorpay rejected the refund request. Check Razorpay balance.",
        }));
      }
    } catch (err) {
      console.error("Execute refund error:", err);
      setRefundModal((prev) => ({
        ...prev,
        processing: false,
        errorMessage: "Network error while connecting to refund server.",
      }));
    }
  };

  // ==========================================================
  // OPEN CREATE ORDER MODAL
  // ==========================================================
  const handleOpenCreateOrderModal = (item) => {
    const notes = item.notes || {};
    const addr = notes.shipping_address || "";
    // Attempt parsing address parts if formatted
    let detectedPin = "";
    const pinMatch = addr.match(/\b\d{6}\b/);
    if (pinMatch) detectedPin = pinMatch[0];

    setCreateOrderModal({
      open: true,
      payment: item,
      fullName: notes.customer_name || item.customer_name || "",
      phone: notes.customer_phone || item.contact ? String(notes.customer_phone || item.contact).replace(/\D/g, "").slice(-10) : "",
      altPhone: notes.alternate_contact && notes.alternate_contact !== "None" ? notes.alternate_contact : "",
      email: item.email || "",
      address: addr || "",
      city: "",
      state: "",
      pinCode: detectedPin,
      amount: String(item.amount || ""),
      itemName: notes.items_summary || "Silk Saree",
      quantity: Number(notes.items_count || 1),
      processing: false,
      errorMessage: "",
      successMessage: "",
      successData: null,
    });
  };

  const handleExecuteCreateOrder = async () => {
    if (!createOrderModal.payment) return;

    if (!createOrderModal.fullName.trim()) {
      setCreateOrderModal((p) => ({ ...p, errorMessage: "Full name is required" }));
      return;
    }
    if (!createOrderModal.phone.trim()) {
      setCreateOrderModal((p) => ({ ...p, errorMessage: "10-digit mobile number is required" }));
      return;
    }
    if (!createOrderModal.address.trim()) {
      setCreateOrderModal((p) => ({ ...p, errorMessage: "Delivery address is required" }));
      return;
    }

    setCreateOrderModal((p) => ({ ...p, processing: true, errorMessage: "", successMessage: "", successData: null }));

    try {
      const itemsPayload = [
        {
          id: 0,
          product_id: 0,
          name: createOrderModal.itemName || "Saree Order",
          price: Number(createOrderModal.amount) || Number(createOrderModal.payment.amount),
          quantity: Number(createOrderModal.quantity || 1),
        },
      ];

      const res = await fetch(`${API_BASE_URL}/orders/razorpay/create-order-from-payment`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          payment_id: createOrderModal.payment.id,
          order_id: createOrderModal.payment.order_id,
          full_name: createOrderModal.fullName.trim(),
          phone: createOrderModal.phone.trim(),
          alt_phone: createOrderModal.altPhone.trim() || null,
          email: createOrderModal.email.trim(),
          address: createOrderModal.address.trim(),
          city: createOrderModal.city.trim(),
          state: createOrderModal.state.trim(),
          pin_code: createOrderModal.pinCode.trim(),
          total_amount: Number(createOrderModal.amount),
          items: itemsPayload,
          shipping_fee: 0,
          discount_amount: 0,
        }),
      });

      const data = await res.json();

      if (res.ok && data.success) {
        const orderNumber = data.orderNumber || "VAN-NEW";
        const linkedPaymentId = createOrderModal.payment.id;

        // 1. Optimistic removal from paymentsWithoutOrders
        setPaymentsWithoutOrders((prev) =>
          prev.filter((p) => p.id !== linkedPaymentId)
        );

        // 2. Optimistic addition to paymentsWithOrders
        setPaymentsWithOrders((prev) => [
          {
            ...createOrderModal.payment,
            order: {
              id: data.orderId || 0,
              order_number: orderNumber,
              full_name: createOrderModal.fullName.trim(),
              phone: createOrderModal.phone.trim(),
              email: createOrderModal.email.trim(),
              total_amount: Number(createOrderModal.amount),
              order_status: "confirmed",
              payment_status: "paid",
              items: itemsPayload,
            },
          },
          ...prev,
        ]);

        // 3. Optimistic stats update
        setStats((prev) => ({
          ...prev,
          withoutOrdersCount: Math.max(0, (prev.withoutOrdersCount || 1) - 1),
          withOrdersCount: (prev.withOrdersCount || 0) + 1,
        }));

        // 4. Set Toast Notification
        setToastNotification({
          type: "success",
          title: "✓ Order Created Successfully!",
          message: `Order #${orderNumber} created in Vanya Database and linked to payment ${linkedPaymentId}.`,
          orderNumber,
          paymentId: linkedPaymentId,
        });

        // 5. Update modal with success data
        setCreateOrderModal((p) => ({
          ...p,
          processing: false,
          errorMessage: "",
          successMessage: `✓ Order ${orderNumber} successfully created and connected in database!`,
          successData: {
            orderNumber,
            paymentId: linkedPaymentId,
          },
        }));

        // 6. Background quiet sync after 2.5s
        setTimeout(() => {
          fetchCollections(false);
        }, 2500);
      } else {
        setCreateOrderModal((p) => ({
          ...p,
          processing: false,
          errorMessage: data.message || "Failed to create order from payment.",
        }));
      }
    } catch (err) {
      console.error("Create order error:", err);
      setCreateOrderModal((p) => ({
        ...p,
        processing: false,
        errorMessage: "Network error while saving order.",
      }));
    }
  };

  // Export to CSV helper
  const handleExportCSV = () => {
    if (!filteredList.length) {
      alert("No data available to export with current filters.");
      return;
    }

    const headers = [
      "Payment ID",
      "Razorpay Order ID",
      "Type",
      "DB Order Number",
      "Customer Name",
      "Customer Phone",
      "Alt Phone",
      "Email",
      "Amount (INR)",
      "Payment Method",
      "Payment Status",
      "Refund Status",
      "Amount Refunded",
      "Date & Time",
      "Shipping Address",
      "Items Summary",
    ];

    const rows = filteredList.map((it) => {
      const isWithOrder = Boolean(it.order);
      const name = it.customer_name || it.order?.full_name || it.notes?.customer_name || "";
      const phone = it.customer_phone || it.contact || it.order?.phone || "";
      const altPhone = it.alternate_contact || it.order?.alt_phone || "";
      const email = it.email || it.order?.email || "";
      const addr = it.shipping_address || it.order?.address || it.notes?.shipping_address || "";
      const summary = it.items_summary || (it.order?.items ? JSON.stringify(it.order.items) : "");

      return [
        `"${it.id || ""}"`,
        `"${it.order_id || ""}"`,
        `"${isWithOrder ? "Connected With Order" : "Without Order (Missing)"}"`,
        `"${it.order?.order_number || "None"}"`,
        `"${name.replace(/"/g, '""')}"`,
        `"${phone}"`,
        `"${altPhone}"`,
        `"${email}"`,
        it.amount || 0,
        `"${it.method || ""}"`,
        `"${it.status || ""}"`,
        `"${it.refund_status || "none"}"`,
        it.amount_refunded || 0,
        `"${formatDateTime(it.created_at)}"`,
        `"${addr.replace(/"/g, '""')}"`,
        `"${summary.replace(/"/g, '""')}"`,
      ].join(",");
    });

    const csvContent = "data:text/csv;charset=utf-8," + [headers.join(","), ...rows].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute(
      "download",
      `vanya_razorpay_${activeTab}_${new Date().toISOString().slice(0, 10)}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="collections-container" style={styles.container}>
      {/* ========================================================
          GLOBAL TOAST CONFIRMATION BANNER (PERSISTENT & VISIBLE)
      ======================================================== */}
      {toastNotification && (
        <div style={styles.toastBanner}>
          <div style={styles.toastIconBox}>
            <CheckCircle2 size={24} color="#059669" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={styles.toastTitle}>{toastNotification.title}</div>
            <div style={styles.toastMessage}>{toastNotification.message}</div>
            <div style={styles.toastMetaRow}>
              {toastNotification.paymentId && (
                <span style={styles.toastMetaItem}>
                  Payment ID: <strong>{toastNotification.paymentId}</strong>
                </span>
              )}
              {toastNotification.refundId && (
                <span style={styles.toastMetaItem}>
                  Razorpay Refund ID: <strong>{toastNotification.refundId}</strong>
                </span>
              )}
              {toastNotification.orderNumber && (
                <span style={styles.toastMetaItem}>
                  Connected Order: <strong>#{toastNotification.orderNumber}</strong>
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setToastNotification(null)}
            style={styles.toastCloseBtn}
            title="Dismiss confirmation"
          >
            <X size={18} />
          </button>
        </div>
      )}

      {/* ========================================================
          PAGE HEADER
      ======================================================== */}
      <div className="collections-header-row" style={styles.headerRow}>
        <div>
          <div style={styles.titleWrap}>
            <div style={styles.titleIconBox}>
              <CreditCard size={24} color="#D4AF37" />
            </div>
            <div>
              <h1 className="collections-page-title" style={styles.pageTitle}>Razorpay Collections & Reconciliation</h1>
              <p style={styles.pageSub}>
                Real-time audit comparing Razorpay captured payments with Vanya Collections database orders.
              </p>
            </div>
          </div>
        </div>

        <div className="collections-header-actions" style={styles.headerActions}>
          <button
            onClick={() => fetchCollections(true)}
            disabled={refreshing || loading}
            style={styles.refreshBtn}
            title="Fetch fresh data from Razorpay & Database"
          >
            <RefreshCw
              size={16}
              style={{
                animation: refreshing ? "spin 0.8s linear infinite" : "none",
              }}
            />
            <span>{refreshing ? "Syncing..." : "Sync Live Data"}</span>
          </button>

          <button
            onClick={handleExportCSV}
            style={styles.exportBtn}
            title="Download current table as CSV"
          >
            <Download size={16} />
            <span>Export CSV</span>
          </button>
        </div>
      </div>

      {/* ========================================================
          EXPLANATION & CAUSE BANNER
      ======================================================== */}
      {showExplanation && (
        <div style={styles.explanationBanner}>
          <div style={styles.explanationHeader}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <ShieldAlert size={20} color="#b45309" />
              <strong style={{ fontSize: "14px", color: "#92400e" }}>
                Reconciliation Intelligence: Why Payments Without Orders Occur
              </strong>
            </div>
            <button
              onClick={() => setShowExplanation(false)}
              style={styles.bannerCloseBtn}
              title="Dismiss"
            >
              <X size={16} />
            </button>
          </div>

          <div style={styles.explanationBody}>
            <p style={{ margin: "0 0 8px 0" }}>
              <strong>The 27 Missing Orders Root Cause:</strong> When customers on mobile pay using UPI (PhonePe, Google Pay, Paytm), their mobile device switches into the banking app. After payment capture, some users immediately swipe away the browser or close the tab before the website's JavaScript callback reaches our server. Without a server-to-server webhook, the payment remained in Razorpay without creating a database order.
            </p>
            <div style={styles.resolutionPills}>
              <span style={styles.resolutionPill}>
                ✓ <strong>Item Details Added to Razorpay Notes:</strong> All future Razorpay orders now include Product IDs, saree names, variants (colour/size), quantities, and full delivery addresses in Razorpay notes.
              </span>
              <span style={styles.resolutionPill}>
                ✓ <strong>Server-to-Server Webhook Active:</strong> The new webhook endpoint automatically captures completed payments even if the user completely closes their browser.
              </span>
              <span style={styles.resolutionPill}>
                ✓ <strong>Direct Action Below:</strong> For the {stats.withoutOrdersCount || 27} payments below, you can click <strong>Direct Refund</strong> to send the money straight back to the customer's UPI/bank account, or <strong>Create Order</strong> to fulfill the shipment!
              </span>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================
          METRIC CARDS ROW
      ======================================================== */}
      <div className="collections-metrics-grid" style={styles.metricsGrid}>
        {/* CARD 1: TOTAL CAPTURED */}
        <div style={styles.metricCard}>
          <div style={styles.metricTop}>
            <span style={styles.metricLabel}>Total Captured in Razorpay</span>
            <div style={{ ...styles.metricIconBox, background: "#fdf8ee", color: "#b45309" }}>
              <CreditCard size={20} />
            </div>
          </div>
          <div style={styles.metricValue}>
            ₹{(stats.totalCapturedAmount || 0).toLocaleString("en-IN")}
          </div>
          <div style={styles.metricSub}>
            <strong>{stats.totalCapturedCount || 0}</strong> total captured transactions in Razorpay
          </div>
        </div>

        {/* CARD 2: CONNECTED WITH ORDERS */}
        <div style={{ ...styles.metricCard, borderLeft: "4px solid #10b981" }}>
          <div style={styles.metricTop}>
            <span style={styles.metricLabel}>Connected with DB Orders</span>
            <div style={{ ...styles.metricIconBox, background: "#ecfdf5", color: "#059669" }}>
              <CheckCircle2 size={20} />
            </div>
          </div>
          <div style={{ ...styles.metricValue, color: "#065f46" }}>
            ₹{(stats.withOrdersAmount || 0).toLocaleString("en-IN")}
          </div>
          <div style={styles.metricSub}>
            <strong>{stats.withOrdersCount || 0}</strong> orders placed & tracked in Vanya database
          </div>
        </div>

        {/* CARD 3: PAYMENTS WITHOUT ORDERS (MISSING) */}
        <div style={{ ...styles.metricCard, borderLeft: "4px solid #dc2626", background: "#fffafa" }}>
          <div style={styles.metricTop}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span style={styles.metricLabel}>Payments Without Orders</span>
              <span style={styles.actionRequiredBadge}>Action Needed</span>
            </div>
            <div style={{ ...styles.metricIconBox, background: "#fef2f2", color: "#dc2626" }}>
              <AlertTriangle size={20} />
            </div>
          </div>
          <div style={{ ...styles.metricValue, color: "#b91c1c" }}>
            ₹{(stats.withoutOrdersAmount || 0).toLocaleString("en-IN")}
          </div>
          <div style={styles.metricSub}>
            <strong>{stats.withoutOrdersCount || 0}</strong> captured payments missing in database
          </div>
        </div>

        {/* CARD 4: REFUNDED AMOUNT */}
        <div style={styles.metricCard}>
          <div style={styles.metricTop}>
            <span style={styles.metricLabel}>Total Refunded</span>
            <div style={{ ...styles.metricIconBox, background: "#eff6ff", color: "#2563eb" }}>
              <RotateCcw size={20} />
            </div>
          </div>
          <div style={styles.metricValue}>
            ₹{(stats.totalRefundedAmount || 0).toLocaleString("en-IN")}
          </div>
          <div style={styles.metricSub}>
            <strong>{stats.totalRefundedCount || 0}</strong> refunds executed via Razorpay
          </div>
        </div>
      </div>

      {/* ========================================================
          TABS NAVIGATION
      ======================================================== */}
      <div className="collections-tabs-container" style={styles.tabsContainer}>
        <button
          onClick={() => setActiveTab("without_orders")}
          style={{
            ...styles.tabBtn,
            ...(activeTab === "without_orders" ? styles.tabBtnActiveRed : {}),
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <AlertTriangle
              size={18}
              color={activeTab === "without_orders" ? "#b91c1c" : "#64748b"}
            />
            <span>Payments WITHOUT Orders (Missing in DB)</span>
            <span
              style={{
                ...styles.tabCounter,
                background: activeTab === "without_orders" ? "#dc2626" : "#cbd5e1",
                color: "#ffffff",
              }}
            >
              {stats.withoutOrdersCount || paymentsWithoutOrders.length}
            </span>
          </div>
        </button>

        <button
          onClick={() => setActiveTab("with_orders")}
          style={{
            ...styles.tabBtn,
            ...(activeTab === "with_orders" ? styles.tabBtnActiveGreen : {}),
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <CheckCircle2
              size={18}
              color={activeTab === "with_orders" ? "#059669" : "#64748b"}
            />
            <span>Payments WITH Orders (Connected)</span>
            <span
              style={{
                ...styles.tabCounter,
                background: activeTab === "with_orders" ? "#10b981" : "#cbd5e1",
                color: "#ffffff",
              }}
            >
              {stats.withOrdersCount || paymentsWithOrders.length}
            </span>
          </div>
        </button>
      </div>

      {/* ========================================================
          SEARCH AND FILTER CONTROLS
      ======================================================== */}
      <div className="collections-filter-card" style={styles.filterCard}>
        <div style={styles.searchBox}>
          <Search size={18} color="#94a3b8" />
          <input
            type="text"
            placeholder="Search by Payment ID, Order ID, Customer Name, Mobile, Amount..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={styles.searchInput}
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              style={styles.clearSearchBtn}
            >
              <X size={15} />
            </button>
          )}
        </div>

        <div className="collections-filter-dropdowns" style={styles.filterDropdowns}>
          {/* Method Filter */}
          <div style={styles.dropdownWrap}>
            <Filter size={14} color="#64748b" />
            <select
              value={methodFilter}
              onChange={(e) => setMethodFilter(e.target.value)}
              style={styles.select}
            >
              <option value="all">All Methods</option>
              <option value="upi">UPI / GPay / PhonePe</option>
              <option value="card">Cards</option>
              <option value="netbanking">Net Banking</option>
              <option value="wallet">Wallets</option>
            </select>
          </div>

          {/* Refund Filter */}
          <div style={styles.dropdownWrap}>
            <RotateCcw size={14} color="#64748b" />
            <select
              value={refundFilter}
              onChange={(e) => setRefundFilter(e.target.value)}
              style={styles.select}
            >
              <option value="all">All Status</option>
              <option value="not_refunded">Not Refunded</option>
              <option value="refunded">Refunded Only</option>
            </select>
          </div>

          {/* Date Filter */}
          <div style={styles.dropdownWrap}>
            <Calendar size={14} color="#64748b" />
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              style={styles.select}
            >
              <option value="all">All Dates</option>
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="week">Past 7 Days</option>
              <option value="month">Past 30 Days</option>
            </select>
          </div>
        </div>
      </div>

      {/* ========================================================
          DATA TABLE
      ======================================================== */}
      <div className="collections-desktop-table-wrap" style={styles.tableCard}>
        <div style={styles.tableTopInfo}>
          <span style={{ fontSize: "13px", color: "#64748b", fontWeight: "600" }}>
            Showing <strong>{filteredList.length}</strong> {activeTab === "without_orders" ? "unmatched payments" : "connected orders"}
          </span>
          {activeTab === "without_orders" && filteredList.length > 0 && (
            <span style={{ fontSize: "12px", color: "#b91c1c", fontWeight: "700" }}>
              💡 Direct Refund will instantly return money to customer's source UPI/Bank via Razorpay.
            </span>
          )}
        </div>

        {loading ? (
          <div style={styles.loadingBox}>
            <RefreshCw
              size={32}
              color="#183B32"
              style={{ animation: "spin 1s linear infinite", marginBottom: "12px" }}
            />
            <p style={{ margin: 0, fontWeight: "600", color: "#183B32" }}>
              Loading Razorpay payments & database orders...
            </p>
          </div>
        ) : filteredList.length === 0 ? (
          <div style={styles.emptyBox}>
            <div style={styles.emptyIconCircle}>
              {activeTab === "without_orders" ? (
                <CheckCircle2 size={36} color="#10b981" />
              ) : (
                <CreditCard size={36} color="#64748b" />
              )}
            </div>
            <h3 style={{ margin: "0 0 6px 0", color: "#183B32", fontSize: "18px" }}>
              {activeTab === "without_orders"
                ? "No Missing Payments Found"
                : "No Connected Payments Found"}
            </h3>
            <p style={{ margin: 0, color: "#64748b", fontSize: "13px" }}>
              {activeTab === "without_orders"
                ? "Every captured Razorpay payment has a matching order in Vanya database."
                : "No payments match the current filter criteria."}
            </p>
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Payment Details</th>
                  <th style={styles.th}>Customer & Contact</th>
                  <th style={styles.th}>Delivery Address</th>
                  <th style={styles.th}>Order Items / Info</th>
                  <th style={styles.th}>Amount</th>
                  <th style={styles.th}>Status</th>
                  <th style={{ ...styles.th, textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredList.map((item) => {
                  const isRefunded =
                    item.refund_status ||
                    (item.amount_refunded && Number(item.amount_refunded) > 0) ||
                    item.status === "refunded";

                  const customerName =
                    item.customer_name ||
                    item.order?.full_name ||
                    item.notes?.customer_name ||
                    "Valued Customer";

                  const phone =
                    item.customer_phone ||
                    item.contact ||
                    item.order?.phone ||
                    "";

                  const altPhone =
                    item.alternate_contact ||
                    item.order?.alt_phone ||
                    item.notes?.alternate_contact;

                  const address =
                    item.shipping_address ||
                    item.order?.address ||
                    item.notes?.shipping_address ||
                    "-";

                  return (
                    <tr key={item.id} style={styles.tr}>
                      {/* 1. PAYMENT DETAILS */}
                      <td style={styles.td}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <span style={styles.paymentIdBadge} title={item.id}>
                              {item.id}
                            </span>
                            <button
                              onClick={() => handleCopy(item.id, item.id)}
                              style={styles.copyBtn}
                              title="Copy Payment ID"
                            >
                              {copiedId === item.id ? (
                                <Check size={12} color="#10b981" />
                              ) : (
                                <Copy size={12} />
                              )}
                            </button>
                          </div>

                          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <span style={styles.methodBadge}>
                              ⚡ {(item.method || "online").toUpperCase()}
                            </span>
                            {item.vpa && (
                              <span style={styles.vpaBadge} title={item.vpa}>
                                {item.vpa}
                              </span>
                            )}
                          </div>

                          <span style={styles.timestampText}>
                            {formatDateTime(item.created_at)}
                          </span>

                          {item.order_id && (
                            <span style={styles.orderIdSub}>
                              RZP Order: {item.order_id}
                            </span>
                          )}
                        </div>
                      </td>

                      {/* 2. CUSTOMER & CONTACT */}
                      <td style={styles.td}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "3px" }}>
                          <span style={styles.customerNameText}>
                            {customerName}
                          </span>

                          {phone && (
                            <a
                              href={`tel:${phone}`}
                              style={styles.phoneLink}
                              title="Click to call"
                            >
                              <Phone size={11} />
                              <span>+91 {phone}</span>
                            </a>
                          )}

                          {altPhone && altPhone !== "None" && altPhone !== "null" && (
                            <span style={styles.altPhoneText}>
                              Alt: {altPhone}
                            </span>
                          )}

                          {item.email && (
                            <span style={styles.emailText} title={item.email}>
                              {item.email}
                            </span>
                          )}
                        </div>
                      </td>

                      {/* 3. DELIVERY ADDRESS */}
                      <td style={styles.td}>
                        <div style={styles.addressBlock} title={address}>
                          <MapPin size={12} style={{ flexShrink: 0, marginTop: "2px" }} />
                          <span style={styles.addressText}>{address}</span>
                        </div>
                      </td>

                      {/* 4. ORDER ITEMS / INFO */}
                      <td style={styles.td}>
                        {activeTab === "with_orders" && item.order ? (
                          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                            <span style={styles.connectedOrderBadge}>
                              Order #{item.order.order_number || `VAN-${item.order.id}`}
                            </span>
                            <span style={{ fontSize: "11px", color: "#64748b" }}>
                              Status: <strong style={{ textTransform: "capitalize", color: "#183B32" }}>{item.order.order_status || "confirmed"}</strong>
                            </span>
                            {item.order.items && Array.isArray(item.order.items) && item.order.items.length > 0 ? (
                              <span
                                style={styles.itemSummaryPill}
                                title={item.order.items.map((it) => `${it.name || "Saree"} (x${it.quantity || 1})`).join("; ")}
                              >
                                {item.order.items.map((it) => `${it.name || "Saree"} (x${it.quantity || 1})`).join(", ").slice(0, 40)}
                                {item.order.items.map((it) => `${it.name || "Saree"} (x${it.quantity || 1})`).join(", ").length > 40 ? "..." : ""}
                              </span>
                            ) : item.items_summary ? (
                              <span style={styles.itemSummaryPill} title={item.items_summary}>
                                {item.items_summary}
                              </span>
                            ) : (
                              <span style={{ fontSize: "11px", color: "#475569" }}>
                                1 item in bag
                              </span>
                            )}
                          </div>
                        ) : (
                          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                            <span style={styles.missingWarningBadge}>
                              ⚠️ Not in Vanya Database
                            </span>
                            <span style={{ fontSize: "11.5px", color: "#475569" }}>
                              Pieces: <strong>{item.items_count || 1}</strong>
                            </span>
                            {item.items_summary && (
                              <span style={styles.itemSummaryPill} title={item.items_summary}>
                                {item.items_summary}
                              </span>
                            )}
                          </div>
                        )}
                      </td>

                      {/* 5. AMOUNT */}
                      <td style={styles.td}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                          <span style={styles.amountText}>
                            ₹{Number(item.amount || 0).toLocaleString("en-IN")}
                          </span>
                          {isRefunded && (
                            <span style={styles.refundAmountText}>
                              Refunded: ₹{Number(item.amount_refunded || item.amount).toLocaleString("en-IN")}
                            </span>
                          )}
                        </div>
                      </td>

                      {/* 6. STATUS */}
                      <td style={styles.td}>
                        {isRefunded ? (
                          <span style={styles.refundedStatusBadge}>
                            <RotateCcw size={11} />
                            Refunded
                          </span>
                        ) : (
                          <span style={styles.capturedStatusBadge}>
                            <CheckCircle2 size={11} />
                            Captured
                          </span>
                        )}
                      </td>

                      {/* 7. ACTIONS */}
                      <td style={{ ...styles.td, textAlign: "right" }}>
                        <div style={styles.actionButtonsCol}>
                          {activeTab === "without_orders" && (
                            <>
                              {!isRefunded ? (
                                <button
                                  onClick={() => handleOpenRefundModal(item)}
                                  style={styles.directRefundBtn}
                                  title="Issue Real-Time Refund via Razorpay"
                                >
                                  <RotateCcw size={13} />
                                  <span>Direct Refund</span>
                                </button>
                              ) : (
                                <span style={styles.alreadyRefundedText}>
                                  Already Refunded
                                </span>
                              )}

                              <button
                                onClick={() => handleOpenCreateOrderModal(item)}
                                style={styles.createOrderBtn}
                                title="Create confirmed order in database from this payment"
                              >
                                <PlusCircle size={13} />
                                <span>Create Order</span>
                              </button>
                            </>
                          )}

                          {activeTab === "with_orders" && !isRefunded && (
                            <button
                              onClick={() => handleOpenRefundModal(item)}
                              style={styles.softRefundBtn}
                              title="Process refund for this order"
                            >
                              <RotateCcw size={12} />
                              <span>Refund</span>
                            </button>
                          )}

                          <button
                            onClick={() => {
                              setInspectModal({ open: true, item });
                              setShowRawJson(false);
                            }}
                            style={styles.inspectBtn}
                            title="Inspect full details & notes"
                          >
                            <Eye size={13} />
                            <span>Inspect</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ========================================================
          MOBILE COLLECTIONS CARDS LIST (DEDICATED RESPONSIVE MOBILE VIEW)
      ======================================================== */}
      <div className="collections-mobile-cards-list">
        {loading ? (
          <div style={styles.loadingBox}>
            <RefreshCw
              size={28}
              color="#183B32"
              style={{ animation: "spin 1s linear infinite", marginBottom: "10px" }}
            />
            <p style={{ margin: 0, fontWeight: "600", color: "#183B32", fontSize: "14px" }}>
              Loading Razorpay payments...
            </p>
          </div>
        ) : filteredList.length === 0 ? (
          <div style={styles.emptyBox}>
            <div style={styles.emptyIconCircle}>
              {activeTab === "without_orders" ? (
                <CheckCircle2 size={32} color="#10b981" />
              ) : (
                <CreditCard size={32} color="#64748b" />
              )}
            </div>
            <h3 style={{ margin: "0 0 6px 0", color: "#183B32", fontSize: "16px" }}>
              {activeTab === "without_orders"
                ? "No Missing Payments Found"
                : "No Connected Payments Found"}
            </h3>
            <p style={{ margin: 0, color: "#64748b", fontSize: "12px" }}>
              {activeTab === "without_orders"
                ? "Every captured Razorpay payment has a matching order in Vanya database."
                : "No payments match the current filter criteria."}
            </p>
          </div>
        ) : (
          filteredList.map((item) => {
            const isRefunded =
              item.refund_status ||
              (item.amount_refunded && Number(item.amount_refunded) > 0) ||
              item.status === "refunded";

            const customerName =
              item.customer_name ||
              item.order?.full_name ||
              item.notes?.customer_name ||
              "Valued Customer";

            const phone =
              item.customer_phone ||
              item.contact ||
              item.order?.phone ||
              "";

            const altPhone =
              item.alternate_contact ||
              item.order?.alt_phone ||
              item.notes?.alternate_contact;

            const address =
              item.shipping_address ||
              item.order?.address ||
              item.notes?.shipping_address ||
              "-";

            const inspectorItems = parseInspectorItems(item);

            return (
              <div
                key={item.id}
                className="collection-mobile-card"
                style={{
                  background: "#ffffff",
                  borderRadius: "14px",
                  border: isRefunded
                    ? "1.5px solid #fed7aa"
                    : activeTab === "without_orders"
                    ? "1.5px solid #fecaca"
                    : "1.5px solid #e2e8f0",
                  padding: "14px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                }}
              >
                {/* TOP HEADER: Payment ID, Copy, Amount & Status */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "8px" }}>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                      <span style={styles.paymentIdBadge}>{item.id}</span>
                      <button
                        onClick={() => handleCopy(item.id, item.id)}
                        style={styles.copyBtn}
                        title="Copy Payment ID"
                      >
                        {copiedId === item.id ? (
                          <Check size={12} color="#10b981" />
                        ) : (
                          <Copy size={12} />
                        )}
                      </button>
                      <span
                        style={{
                          fontSize: "10px",
                          fontWeight: "700",
                          padding: "2px 6px",
                          borderRadius: "4px",
                          backgroundColor: "#f1f5f9",
                          color: "#475569",
                          textTransform: "uppercase",
                        }}
                      >
                        {item.method || "ONLINE"}
                      </span>
                    </div>
                    <div style={{ fontSize: "11px", color: "#64748b", marginTop: "4px" }}>
                      {formatDateTime(item.created_at)}
                    </div>
                  </div>

                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: "18px", fontWeight: "800", color: "#183B32" }}>
                      ₹{Number(item.amount || 0).toLocaleString("en-IN")}
                    </div>
                    <div>
                      {isRefunded ? (
                        <span
                          style={{
                            fontSize: "10px",
                            fontWeight: "800",
                            backgroundColor: "#ffedd5",
                            color: "#c2410c",
                            padding: "2px 6px",
                            borderRadius: "10px",
                            display: "inline-block",
                            marginTop: "2px",
                          }}
                        >
                          ✓ REFUNDED
                        </span>
                      ) : activeTab === "without_orders" ? (
                        <span
                          style={{
                            fontSize: "10px",
                            fontWeight: "800",
                            backgroundColor: "#fee2e2",
                            color: "#b91c1c",
                            padding: "2px 6px",
                            borderRadius: "10px",
                            display: "inline-block",
                            marginTop: "2px",
                          }}
                        >
                          ⚠️ NO DB ORDER
                        </span>
                      ) : (
                        <span
                          style={{
                            fontSize: "10px",
                            fontWeight: "800",
                            backgroundColor: "#ecfdf5",
                            color: "#059669",
                            padding: "2px 6px",
                            borderRadius: "10px",
                            display: "inline-block",
                            marginTop: "2px",
                          }}
                        >
                          ✓ CONNECTED
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* CUSTOMER & CONTACT DETAILS */}
                <div
                  style={{
                    backgroundColor: "#f8fafc",
                    padding: "10px 12px",
                    borderRadius: "10px",
                    border: "1px solid #eef2f6",
                    fontSize: "12px",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
                    <strong style={{ fontSize: "13px", color: "#1e293b" }}>{customerName}</strong>
                    {item.order?.order_number && (
                      <span style={{ fontSize: "11px", color: "#065f46", fontWeight: "700" }}>
                        Order #{item.order.order_number}
                      </span>
                    )}
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", marginTop: "4px" }}>
                    {phone && (
                      <a
                        href={`tel:${phone}`}
                        style={{
                          color: "#183B32",
                          textDecoration: "none",
                          fontWeight: "700",
                          backgroundColor: "#e8f0fe",
                          padding: "2px 8px",
                          borderRadius: "6px",
                          fontSize: "11.5px",
                        }}
                      >
                        📞 {phone}
                      </a>
                    )}
                    {altPhone && (
                      <a
                        href={`tel:${altPhone}`}
                        style={{
                          color: "#0284c7",
                          textDecoration: "none",
                          fontWeight: "600",
                          backgroundColor: "#e0f2fe",
                          padding: "2px 8px",
                          borderRadius: "6px",
                          fontSize: "11.5px",
                        }}
                      >
                        📱 Alt: {altPhone}
                      </a>
                    )}
                  </div>

                  {address && address !== "-" && (
                    <div style={{ fontSize: "11px", color: "#64748b", marginTop: "6px", lineHeight: "1.35" }}>
                      📍 {address}
                    </div>
                  )}
                </div>

                {/* ORDER ITEMS SUMMARY */}
                {inspectorItems.length > 0 && (
                  <div style={{ fontSize: "11.5px" }}>
                    <div style={{ fontSize: "10.5px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", marginBottom: "4px" }}>
                      Items ({inspectorItems.length})
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                      {inspectorItems.map((it, idx) => (
                        <div
                          key={idx}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            backgroundColor: "#fdfdfd",
                            border: "1px solid #f1f5f9",
                            padding: "5px 8px",
                            borderRadius: "6px",
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: "5px", overflow: "hidden" }}>
                            {it.productId && (
                              <span style={{ fontSize: "9.5px", fontWeight: "700", backgroundColor: "#183B32", color: "#fff", padding: "1px 4px", borderRadius: "3px" }}>
                                #{it.productId}
                              </span>
                            )}
                            <span style={{ fontWeight: "600", color: "#334155", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "180px" }}>
                              {it.name}
                            </span>
                            {it.colour && (
                              <span style={{ color: "#0284c7", fontSize: "10px", fontWeight: "600" }}>
                                • {it.colour}
                              </span>
                            )}
                          </div>
                          <span style={{ fontWeight: "700", color: "#64748b", flexShrink: 0 }}>
                            x{it.quantity}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* ACTIONS BAR */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    paddingTop: "8px",
                    borderTop: "1px solid #f1f5f9",
                    flexWrap: "wrap",
                  }}
                >
                  {isRefunded ? (
                    <div style={{ flex: 1, fontSize: "12px", color: "#c2410c", fontWeight: "600" }}>
                      ✓ ₹{Number(item.amount_refunded || item.amount).toLocaleString("en-IN")} Refunded
                    </div>
                  ) : activeTab === "without_orders" ? (
                    <>
                      <button
                        type="button"
                        onClick={() => handleOpenRefundModal(item)}
                        style={{
                          flex: 1,
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "6px",
                          backgroundColor: "#dc2626",
                          color: "#ffffff",
                          border: "none",
                          borderRadius: "8px",
                          padding: "8px 12px",
                          fontSize: "12px",
                          fontWeight: "700",
                          cursor: "pointer",
                        }}
                      >
                        <RotateCcw size={14} />
                        <span>Direct Refund</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => handleOpenCreateOrderModal(item)}
                        style={{
                          flex: 1,
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "6px",
                          backgroundColor: "#183B32",
                          color: "#D4AF37",
                          border: "none",
                          borderRadius: "8px",
                          padding: "8px 12px",
                          fontSize: "12px",
                          fontWeight: "700",
                          cursor: "pointer",
                        }}
                      >
                        <PlusCircle size={14} />
                        <span>Create Order</span>
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleOpenRefundModal(item)}
                      style={{
                        flex: 1,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "6px",
                        backgroundColor: "#fff1f2",
                        color: "#e11d48",
                        border: "1px solid #fecdd3",
                        borderRadius: "8px",
                        padding: "8px 12px",
                        fontSize: "12px",
                        fontWeight: "600",
                        cursor: "pointer",
                      }}
                    >
                      <RotateCcw size={13} />
                      <span>Refund Order</span>
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={() => {
                      setInspectModal({ open: true, item });
                      setShowRawJson(false);
                    }}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "4px",
                      backgroundColor: "#f8fafc",
                      color: "#475569",
                      border: "1px solid #cbd5e1",
                      borderRadius: "8px",
                      padding: "8px 12px",
                      fontSize: "12px",
                      fontWeight: "600",
                      cursor: "pointer",
                    }}
                    title="Inspect details"
                  >
                    <Eye size={14} />
                    <span>Inspect</span>
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* ========================================================
          HIGH PROMPT DIRECT REFUND MODAL
      ======================================================== */}
      {refundModal.open && refundModal.payment && (
        <div style={styles.modalOverlay}>
          <div className="modal-responsive-card refund-modal-box" style={styles.refundModalBox}>
            {refundModal.successData ? (
              <div style={styles.successModalBody}>
                <div style={styles.successIconCircle}>
                  <CheckCircle2 size={42} color="#059669" />
                </div>
                <h2 style={styles.successModalTitle}>
                  Refund Processed Successfully!
                </h2>
                <p style={styles.successModalSubtitle}>
                  ₹{Number(refundModal.successData.amount || 0).toLocaleString("en-IN")} was sent back to the customer's original UPI/bank account via Razorpay.
                </p>

                <div style={styles.successReceiptCard}>
                  <div style={styles.modalSummaryRow}>
                    <span style={styles.modalSummaryLabel}>Razorpay Refund ID:</span>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <span style={styles.modalSummaryValueMono}>
                        {refundModal.successData.refundId}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleCopy(refundModal.successData.refundId, "refund-modal-id")}
                        style={styles.copyBtn}
                        title="Copy Refund ID"
                      >
                        {copiedId === "refund-modal-id" ? (
                          <Check size={12} color="#10b981" />
                        ) : (
                          <Copy size={12} />
                        )}
                      </button>
                    </div>
                  </div>

                  <div style={styles.modalSummaryRow}>
                    <span style={styles.modalSummaryLabel}>Payment ID:</span>
                    <span style={styles.modalSummaryValueMono}>
                      {refundModal.successData.paymentId}
                    </span>
                  </div>

                  <div style={styles.modalSummaryRow}>
                    <span style={styles.modalSummaryLabel}>Refund Amount:</span>
                    <span style={{ fontSize: "16px", fontWeight: "800", color: "#065f46" }}>
                      ₹{Number(refundModal.successData.amount).toLocaleString("en-IN")}
                    </span>
                  </div>

                  <div style={styles.modalSummaryRow}>
                    <span style={styles.modalSummaryLabel}>Customer:</span>
                    <span style={styles.modalSummaryValue}>
                      {refundModal.successData.customer}
                    </span>
                  </div>

                  <div style={styles.modalSummaryRow}>
                    <span style={styles.modalSummaryLabel}>Table Status:</span>
                    <span style={{ fontSize: "12.5px", fontWeight: "700", color: "#059669", display: "flex", alignItems: "center", gap: "4px" }}>
                      <CheckCircle2 size={13} /> Updated to "Refunded"
                    </span>
                  </div>
                </div>

                <div style={{ padding: "0 24px 24px" }}>
                  <button
                    type="button"
                    onClick={() =>
                      setRefundModal({
                        open: false,
                        payment: null,
                        refundAmount: "",
                        refundReason: "",
                        confirmText: "",
                        processing: false,
                        errorMessage: "",
                        successMessage: "",
                        successData: null,
                      })
                    }
                    style={styles.successCloseBtn}
                  >
                    Done & Return to Collections
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* ALERT HEADER */}
                <div style={styles.refundModalHeader}>
                  <div style={styles.alertIconCircle}>
                    <ShieldAlert size={28} color="#ffffff" />
                  </div>
                  <h2 style={styles.refundModalTitle}>
                    HIGH ALERT: Direct Real-Time Refund
                  </h2>
                  <p style={styles.refundModalSub}>
                    This action initiates an instant Razorpay API refund back to the customer's source account.
                  </p>
                </div>

                <div style={styles.refundModalBody}>
                  {/* WARNING BOX */}
                  <div style={styles.criticalWarningBox}>
                    <AlertTriangle size={20} color="#b91c1c" style={{ flexShrink: 0 }} />
                    <div style={{ fontSize: "12.5px", color: "#7f1d1d", lineHeight: "1.45" }}>
                      <strong>CAUTION:</strong> Money will be immediately deducted from your Razorpay balance and returned to the customer's original UPI/bank account. <strong>This transaction CANNOT be reversed or undone.</strong>
                    </div>
                  </div>

                  {/* PAYMENT SUMMARY CARD */}
                  <div style={styles.modalSummaryCard}>
                    <div style={styles.modalSummaryRow}>
                      <span style={styles.modalSummaryLabel}>Payment ID:</span>
                      <span style={styles.modalSummaryValueMono}>
                        {refundModal.payment.id}
                      </span>
                    </div>

                    <div style={styles.modalSummaryRow}>
                      <span style={styles.modalSummaryLabel}>Customer:</span>
                      <span style={styles.modalSummaryValue}>
                        {refundModal.payment.customer_name ||
                          refundModal.payment.order?.full_name ||
                          "Valued Customer"} (+91 {refundModal.payment.customer_phone || refundModal.payment.contact || "N/A"})
                      </span>
                    </div>

                    <div style={styles.modalSummaryRow}>
                      <span style={styles.modalSummaryLabel}>Original Payment:</span>
                      <span style={styles.modalSummaryValueHighlight}>
                        ₹{Number(refundModal.payment.amount || 0).toLocaleString("en-IN")} via {(refundModal.payment.method || "UPI").toUpperCase()}
                      </span>
                    </div>

                    {refundModal.payment.order_id && (
                      <div style={styles.modalSummaryRow}>
                        <span style={styles.modalSummaryLabel}>RZP Order ID:</span>
                        <span style={styles.modalSummaryValueMono}>
                          {refundModal.payment.order_id}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* REFUND AMOUNT INPUT */}
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>Refund Amount (₹)</label>
                    <input
                      type="number"
                      value={refundModal.refundAmount}
                      onChange={(e) =>
                        setRefundModal((p) => ({ ...p, refundAmount: e.target.value }))
                      }
                      style={styles.modalInput}
                      placeholder="Enter amount to refund"
                    />
                    <span style={{ fontSize: "11px", color: "#64748b" }}>
                      Defaults to full captured balance. You can enter a partial amount if applicable.
                    </span>
                  </div>

                  {/* REFUND REASON */}
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>Reason for Refund</label>
                    <select
                      value={refundModal.refundReason}
                      onChange={(e) =>
                        setRefundModal((p) => ({ ...p, refundReason: e.target.value }))
                      }
                      style={styles.modalInput}
                    >
                      <option value="Payment captured without order in database">
                        Payment captured without order in database (Reconciliation)
                      </option>
                      <option value="Customer requested order cancellation">
                        Customer requested order cancellation
                      </option>
                      <option value="Product out of stock / unable to fulfill">
                        Product out of stock / unable to fulfill
                      </option>
                      <option value="Duplicate payment made by customer">
                        Duplicate payment made by customer
                      </option>
                      <option value="Other administrative refund">
                        Other administrative refund
                      </option>
                    </select>
                  </div>

                  {/* CONFIRMATION SAFETY PROMPT */}
                  <div style={styles.confirmationSafetyBox}>
                    <label style={{ fontSize: "12px", fontWeight: "700", color: "#991b1b" }}>
                      TYPE <span style={{ textDecoration: "underline" }}>REFUND</span> IN CAPITAL LETTERS TO UNLOCK CONFIRMATION:
                    </label>
                    <input
                      type="text"
                      placeholder="Type REFUND"
                      value={refundModal.confirmText}
                      onChange={(e) =>
                        setRefundModal((p) => ({ ...p, confirmText: e.target.value }))
                      }
                      style={styles.confirmPromptInput}
                    />
                  </div>

                  {refundModal.errorMessage && (
                    <div style={styles.modalErrorBox}>
                      ✕ {refundModal.errorMessage}
                    </div>
                  )}

                  {refundModal.successMessage && (
                    <div style={styles.modalSuccessBox}>
                      {refundModal.successMessage}
                    </div>
                  )}
                </div>

                {/* MODAL ACTIONS */}
                <div style={styles.modalFooter}>
                  <button
                    type="button"
                    onClick={() => setRefundModal((p) => ({ ...p, open: false }))}
                    disabled={refundModal.processing}
                    style={styles.cancelBtn}
                  >
                    Cancel / Keep Payment
                  </button>

                  <button
                    type="button"
                    onClick={handleExecuteRefund}
                    disabled={
                      refundModal.processing ||
                      refundModal.confirmText.trim().toUpperCase() !== "REFUND"
                    }
                    style={{
                      ...styles.confirmRefundBtn,
                      opacity:
                        refundModal.confirmText.trim().toUpperCase() === "REFUND"
                          ? 1
                          : 0.5,
                      cursor:
                        refundModal.confirmText.trim().toUpperCase() === "REFUND"
                          ? "pointer"
                          : "not-allowed",
                    }}
                  >
                    {refundModal.processing ? (
                      <>
                        <RefreshCw size={16} style={{ animation: "spin 0.8s linear infinite" }} />
                        <span>Processing Refund via Razorpay...</span>
                      </>
                    ) : (
                      <>
                        <span>⚠️ Confirm & Issue Real-Time Refund</span>
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ========================================================
          CREATE ORDER FROM PAYMENT MODAL
      ======================================================== */}
      {createOrderModal.open && createOrderModal.payment && (
        <div style={styles.modalOverlay}>
          <div className="modal-responsive-card create-order-modal-box" style={styles.createOrderModalBox}>
            {createOrderModal.successData ? (
              <div style={styles.successModalBody}>
                <div style={styles.successIconCircle}>
                  <CheckCircle2 size={42} color="#059669" />
                </div>
                <h2 style={styles.successModalTitle}>
                  Order Created Successfully!
                </h2>
                <p style={styles.successModalSubtitle}>
                  Order #{createOrderModal.successData.orderNumber} is now live in Vanya Database and linked to Razorpay payment {createOrderModal.successData.paymentId}.
                </p>

                <div style={styles.successReceiptCard}>
                  <div style={styles.modalSummaryRow}>
                    <span style={styles.modalSummaryLabel}>Order Number:</span>
                    <strong style={{ fontSize: "15px", color: "#183B32" }}>
                      #{createOrderModal.successData.orderNumber}
                    </strong>
                  </div>

                  <div style={styles.modalSummaryRow}>
                    <span style={styles.modalSummaryLabel}>Payment ID:</span>
                    <span style={styles.modalSummaryValueMono}>
                      {createOrderModal.successData.paymentId}
                    </span>
                  </div>

                  <div style={styles.modalSummaryRow}>
                    <span style={styles.modalSummaryLabel}>Status in Table:</span>
                    <span style={{ fontSize: "12.5px", fontWeight: "700", color: "#059669", display: "flex", alignItems: "center", gap: "4px" }}>
                      <CheckCircle2 size={13} /> Connected with DB Order
                    </span>
                  </div>
                </div>

                <div style={{ padding: "0 24px 24px" }}>
                  <button
                    type="button"
                    onClick={() =>
                      setCreateOrderModal((p) => ({
                        ...p,
                        open: false,
                        successData: null,
                      }))
                    }
                    style={styles.successCloseBtn}
                  >
                    Done & Return to Collections
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div style={styles.createOrderHeader}>
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <PlusCircle size={22} color="#D4AF37" />
                    <h3 style={{ margin: 0, color: "#ffffff", fontSize: "18px" }}>
                      Create Order from Payment ({createOrderModal.payment.id})
                    </h3>
                  </div>
                  <button
                    onClick={() => setCreateOrderModal((p) => ({ ...p, open: false }))}
                    style={styles.modalCloseBtn}
                  >
                    <X size={18} />
                  </button>
                </div>

                <div style={styles.createOrderBody}>
                  <p style={{ margin: "0 0 16px 0", fontSize: "13px", color: "#64748b" }}>
                    Pre-filled from Razorpay payment notes. Saving this creates a confirmed order in <strong>vanyaorders</strong> and links it to this payment.
                  </p>

                  <div style={styles.formGrid2}>
                    <div style={styles.formGroup}>
                      <label style={styles.formLabel}>Customer Full Name *</label>
                      <input
                        type="text"
                        value={createOrderModal.fullName}
                        onChange={(e) =>
                          setCreateOrderModal((p) => ({ ...p, fullName: e.target.value }))
                        }
                        style={styles.modalInput}
                      />
                    </div>

                    <div style={styles.formGroup}>
                      <label style={styles.formLabel}>Customer Mobile Number *</label>
                      <input
                        type="tel"
                        value={createOrderModal.phone}
                        onChange={(e) =>
                          setCreateOrderModal((p) => ({ ...p, phone: e.target.value }))
                        }
                        style={styles.modalInput}
                      />
                    </div>

                    <div style={styles.formGroup}>
                      <label style={styles.formLabel}>Alternate Contact Number (Optional)</label>
                      <input
                        type="tel"
                        value={createOrderModal.altPhone}
                        onChange={(e) =>
                          setCreateOrderModal((p) => ({ ...p, altPhone: e.target.value }))
                        }
                        style={styles.modalInput}
                      />
                    </div>

                    <div style={styles.formGroup}>
                      <label style={styles.formLabel}>Email Address</label>
                      <input
                        type="email"
                        value={createOrderModal.email}
                        onChange={(e) =>
                          setCreateOrderModal((p) => ({ ...p, email: e.target.value }))
                        }
                        style={styles.modalInput}
                      />
                    </div>

                    <div style={{ ...styles.formGroup, gridColumn: "1 / -1" }}>
                      <label style={styles.formLabel}>Delivery Address *</label>
                      <textarea
                        rows={2}
                        value={createOrderModal.address}
                        onChange={(e) =>
                          setCreateOrderModal((p) => ({ ...p, address: e.target.value }))
                        }
                        style={styles.modalInput}
                      />
                    </div>

                    <div style={styles.formGroup}>
                      <label style={styles.formLabel}>PIN Code</label>
                      <input
                        type="text"
                        value={createOrderModal.pinCode}
                        onChange={(e) =>
                          setCreateOrderModal((p) => ({ ...p, pinCode: e.target.value }))
                        }
                        style={styles.modalInput}
                      />
                    </div>

                    <div style={styles.formGroup}>
                      <label style={styles.formLabel}>Amount (₹)</label>
                      <input
                        type="number"
                        value={createOrderModal.amount}
                        onChange={(e) =>
                          setCreateOrderModal((p) => ({ ...p, amount: e.target.value }))
                        }
                        style={styles.modalInput}
                      />
                    </div>

                    <div style={styles.formGroup}>
                      <label style={styles.formLabel}>Item Description / Saree Name</label>
                      <input
                        type="text"
                        value={createOrderModal.itemName}
                        onChange={(e) =>
                          setCreateOrderModal((p) => ({ ...p, itemName: e.target.value }))
                        }
                        style={styles.modalInput}
                      />
                    </div>

                    <div style={styles.formGroup}>
                      <label style={styles.formLabel}>Pieces / Quantity</label>
                      <input
                        type="number"
                        min={1}
                        value={createOrderModal.quantity}
                        onChange={(e) =>
                          setCreateOrderModal((p) => ({ ...p, quantity: e.target.value }))
                        }
                        style={styles.modalInput}
                      />
                    </div>
                  </div>

                  {createOrderModal.errorMessage && (
                    <div style={styles.modalErrorBox}>
                      ✕ {createOrderModal.errorMessage}
                    </div>
                  )}

                  {createOrderModal.successMessage && (
                    <div style={styles.modalSuccessBox}>
                      {createOrderModal.successMessage}
                    </div>
                  )}
                </div>

                <div style={styles.modalFooter}>
                  <button
                    type="button"
                    onClick={() => setCreateOrderModal((p) => ({ ...p, open: false }))}
                    style={styles.cancelBtn}
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    onClick={handleExecuteCreateOrder}
                    disabled={createOrderModal.processing}
                    style={styles.confirmGoldBtn}
                  >
                    {createOrderModal.processing ? "Creating Order..." : "Confirm & Save Order to Database"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ========================================================
          INSPECT MODAL (FULL PAYLOAD & NOTES VIEWER)
      ======================================================== */}
      {inspectModal.open && inspectModal.item && (() => {
        const item = inspectModal.item;
        const order = item.order;
        const isRefunded =
          item.refund_status ||
          (item.amount_refunded && Number(item.amount_refunded) > 0) ||
          item.status === "refunded";
        const parsedItems = parseInspectorItems(item);

        const recipientName =
          order?.full_name ||
          item.customer_name ||
          item.notes?.customer_name ||
          "Valued Customer";
        const recipientPhone =
          order?.phone || item.customer_phone || item.contact || "";
        const altPhone =
          order?.alt_phone ||
          item.alternate_contact ||
          item.notes?.alternate_contact;
        const email = order?.email || item.email || "";
        const address = order?.address
          ? `${order.address}, ${order.city || ""}, ${order.state || ""}${
              order.pin_code ? ` - ${order.pin_code}` : ""
            }`
          : item.shipping_address ||
            item.notes?.shipping_address ||
            "No address provided";

        return (
          <div
            style={styles.modalOverlay}
            onClick={() => setInspectModal({ open: false, item: null })}
          >
            <div
              className="modal-responsive-card inspect-modal-box"
              style={styles.inspectModalBox}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={styles.inspectHeader}>
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <div style={styles.inspectIconBox}>
                    <Eye size={20} color="#D4AF37" />
                  </div>
                  <div>
                    <h3 style={styles.inspectHeaderTitle}>
                      Payment & Order Inspector
                    </h3>
                    <div style={styles.inspectHeaderSub}>
                      Payment ID:{" "}
                      <strong style={{ color: "#183B32", fontFamily: "monospace" }}>
                        {item.id}
                      </strong>{" "}
                      • Captured at {formatDateTime(item.created_at)}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setInspectModal({ open: false, item: null })}
                  style={styles.modalCloseBtnDark}
                  title="Close Inspector"
                >
                  <X size={18} />
                </button>
              </div>

              <div style={styles.inspectBody}>
                {/* 1. KEY TRANSACTION SUMMARY */}
                <div style={styles.inspectSummaryGrid}>
                  <div style={styles.inspectSummaryCell}>
                    <span style={styles.inspectSummaryLabel}>Captured Amount</span>
                    <span style={styles.inspectSummaryValueAmount}>
                      ₹{Number(item.amount || 0).toLocaleString("en-IN")}
                    </span>
                  </div>

                  <div style={styles.inspectSummaryCell}>
                    <span style={styles.inspectSummaryLabel}>Gateway Status</span>
                    <div>
                      {isRefunded ? (
                        <span style={styles.refundedStatusBadge}>
                          <RotateCcw size={11} />
                          Refunded (₹{Number(item.amount_refunded || item.amount).toLocaleString("en-IN")})
                        </span>
                      ) : (
                        <span style={styles.capturedStatusBadge}>
                          <CheckCircle2 size={11} />
                          Captured
                        </span>
                      )}
                    </div>
                  </div>

                  <div style={styles.inspectSummaryCell}>
                    <span style={styles.inspectSummaryLabel}>Payment Method</span>
                    <span style={styles.inspectSummaryValueMethod}>
                      ⚡ {(item.method || "UPI").toUpperCase()} {item.vpa ? `(${item.vpa})` : ""}
                    </span>
                  </div>

                  <div style={styles.inspectSummaryCell}>
                    <span style={styles.inspectSummaryLabel}>Database Connection</span>
                    <div>
                      {order ? (
                        <span style={styles.connectedOrderBadge}>
                          Order #{order.order_number || `VAN-${order.id}`}
                        </span>
                      ) : (
                        <span style={styles.missingWarningBadge}>
                          ⚠️ Not in Database
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* 2. CUSTOMER & DELIVERY ADDRESS (CLEAR HUMAN-FRIENDLY GROUPING) */}
                <div style={styles.inspectCard}>
                  <div style={styles.inspectCardHeader}>
                    <MapPin size={16} color="#183B32" />
                    <strong style={{ fontSize: "13.5px", color: "#183B32" }}>
                      Customer & Delivery Address
                    </strong>
                  </div>

                  <div style={styles.inspectCardContent}>
                    <div style={styles.inspectDetailRow}>
                      <span style={styles.inspectDetailLabel}>Recipient:</span>
                      <strong style={styles.inspectDetailValBold}>
                        {recipientName} {recipientPhone ? `(+91 ${recipientPhone})` : ""}
                      </strong>
                    </div>

                    {altPhone && altPhone !== "None" && altPhone !== "null" && (
                      <div style={styles.inspectDetailRow}>
                        <span style={styles.inspectDetailLabel}>Alt Contact:</span>
                        <span style={styles.inspectDetailVal}>{altPhone}</span>
                      </div>
                    )}

                    {email && (
                      <div style={styles.inspectDetailRow}>
                        <span style={styles.inspectDetailLabel}>Email:</span>
                        <span style={styles.inspectDetailVal}>{email}</span>
                      </div>
                    )}

                    <div style={{ ...styles.inspectDetailRow, alignItems: "flex-start" }}>
                      <span style={styles.inspectDetailLabel}>Delivery Address:</span>
                      <span style={styles.inspectAddressText}>{address}</span>
                    </div>

                    {order && (
                      <div style={styles.inspectDetailRow}>
                        <span style={styles.inspectDetailLabel}>Order Status:</span>
                        <span style={styles.inspectOrderStatusPill}>
                          {order.order_status || "confirmed"} (Payment: {order.payment_status || "paid"})
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* 3. ORDERED PRODUCTS (CLARITY PRODUCT CARDS - NO RAW JSON DUMP) */}
                <div style={styles.inspectCard}>
                  <div style={styles.inspectCardHeader}>
                    <Package size={16} color="#183B32" />
                    <strong style={{ fontSize: "13.5px", color: "#183B32" }}>
                      Ordered Products ({parsedItems.length} {parsedItems.length === 1 ? "Item" : "Items"})
                    </strong>
                  </div>

                  <div style={styles.inspectCardContent}>
                    {parsedItems.length > 0 ? (
                      <div style={styles.orderedItemsList}>
                        {parsedItems.map((prod, idx) => (
                          <div key={idx} style={styles.orderedItemCard}>
                            {prod.image ? (
                              <img
                                src={prod.image}
                                alt={prod.name}
                                style={styles.orderedItemImg}
                                onError={(e) => {
                                  e.target.style.display = "none";
                                }}
                              />
                            ) : (
                              <div style={styles.orderedItemImgPlaceholder}>
                                <Package size={22} color="#183B32" />
                              </div>
                            )}

                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={styles.orderedItemName}>{prod.name}</div>
                              <div style={styles.orderedItemMetaRow}>
                                {prod.productId && (
                                  <span style={styles.orderedItemBadgeId}>
                                    Product ID: #{prod.productId}
                                  </span>
                                )}
                                {prod.colour && (
                                  <span style={styles.orderedItemBadgeVariant}>
                                    Colour: {prod.colour}
                                  </span>
                                )}
                                {prod.size && (
                                  <span style={styles.orderedItemBadgeVariant}>
                                    Size: {prod.size}
                                  </span>
                                )}
                              </div>
                            </div>

                            <div style={styles.orderedItemPricing}>
                              <span style={styles.orderedItemQtyBadge}>
                                Qty: {prod.quantity}
                              </span>
                              {prod.price !== null && (
                                <span style={styles.orderedItemPrice}>
                                  ₹{Number(prod.price).toLocaleString("en-IN")}
                                </span>
                              )}
                              {prod.price !== null && prod.quantity > 1 && (
                                <span style={styles.orderedItemSubtotal}>
                                  Total: ₹{(prod.price * prod.quantity).toLocaleString("en-IN")}
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ color: "#64748b", fontSize: "13px", padding: "8px 0" }}>
                        No individual items specified. See notes summary below.
                      </div>
                    )}
                  </div>
                </div>

                {/* 4. RAZORPAY GATEWAY NOTES PAYLOAD */}
                {item.notes && Object.keys(item.notes).length > 0 && (
                  <div style={styles.inspectCard}>
                    <div style={styles.inspectCardHeader}>
                      <FileText size={16} color="#183B32" />
                      <strong style={{ fontSize: "13.5px", color: "#183B32" }}>
                        Razorpay Gateway Notes Payload
                      </strong>
                    </div>

                    <div style={styles.inspectCardContent}>
                      <div style={styles.notesTable}>
                        {Object.entries(item.notes).map(([k, v]) => {
                          const label =
                            k === "customer_name"
                              ? "Customer Name"
                              : k === "customer_phone"
                              ? "Phone Number"
                              : k === "alternate_contact"
                              ? "Alternate Contact"
                              : k === "shipping_address"
                              ? "Shipping Address"
                              : k === "items_count"
                              ? "Total Items Count"
                              : k === "items_summary"
                              ? "Items Summary"
                              : k.startsWith("item_")
                              ? `Ordered Item #${k.replace("item_", "")}`
                              : k;

                          return (
                            <div key={k} style={styles.notesRow}>
                              <strong style={styles.notesKey}>{label}:</strong>
                              <span style={styles.notesVal}>{String(v)}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}

                {/* 5. COLLAPSIBLE TECHNICAL RAW JSON */}
                <div style={{ marginTop: "6px", flexShrink: 0 }}>
                  <button
                    type="button"
                    onClick={() => setShowRawJson((prev) => !prev)}
                    style={styles.toggleRawJsonBtn}
                  >
                    {showRawJson ? (
                      <>
                        <ChevronUp size={15} />
                        <span>Hide Developer Raw JSON</span>
                      </>
                    ) : (
                      <>
                        <ChevronDown size={15} />
                        <span>View Developer Raw JSON (Technical Payload)</span>
                      </>
                    )}
                  </button>

                  {showRawJson && (
                    <div style={styles.rawJsonBox}>
                      <div style={styles.rawJsonHeader}>
                        <span style={{ fontSize: "12px", color: "#94a3b8" }}>
                          Full Transaction Payload
                        </span>
                        <button
                          type="button"
                          onClick={() =>
                            handleCopy(
                              JSON.stringify(item, null, 2),
                              "raw-json-copy"
                            )
                          }
                          style={styles.copyBtn}
                          title="Copy JSON"
                        >
                          {copiedId === "raw-json-copy" ? (
                            <Check size={12} color="#10b981" />
                          ) : (
                            <Copy size={12} color="#ffffff" />
                          )}
                        </button>
                      </div>
                      <pre style={styles.preCode}>
                        {JSON.stringify(item, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              </div>

              <div style={styles.modalFooter}>
                <button
                  onClick={() => setInspectModal({ open: false, item: null })}
                  style={styles.cancelBtn}
                >
                  Close Inspector
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* MOBILE RESPONSIVE MEDIA QUERIES FOR RAZORPAY COLLECTIONS */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @media (max-width: 900px) {
          .collections-metrics-grid {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 10px !important;
          }
        }

        @media (max-width: 768px) {
          .collections-container {
            padding: 12px 10px !important;
            gap: 14px !important;
          }
          .collections-header-row {
            flex-direction: column !important;
            align-items: stretch !important;
            gap: 12px !important;
          }
          .collections-page-title {
            font-size: 19px !important;
            line-height: 1.25 !important;
          }
          .collections-header-actions {
            width: 100% !important;
            display: flex !important;
            gap: 8px !important;
          }
          .collections-header-actions button {
            flex: 1 !important;
            justify-content: center !important;
            padding: 10px 8px !important;
            font-size: 12px !important;
          }
          .collections-tabs-container {
            flex-direction: column !important;
            gap: 8px !important;
          }
          .collections-tabs-container button {
            width: 100% !important;
            justify-content: space-between !important;
            padding: 10px 14px !important;
          }
          .collections-filter-card {
            padding: 12px !important;
            gap: 10px !important;
          }
          .collections-filter-dropdowns {
            display: grid !important;
            grid-template-columns: 1fr 1fr !important;
            gap: 8px !important;
            width: 100% !important;
          }
          .collections-filter-dropdowns > div {
            width: 100% !important;
            box-sizing: border-box !important;
          }
          .collections-desktop-table-wrap {
            display: none !important;
          }
          .collections-mobile-cards-list {
            display: flex !important;
            flex-direction: column !important;
            gap: 12px !important;
          }
          .modal-responsive-card {
            max-width: 95vw !important;
            max-height: 94vh !important;
            overflow-y: auto !important;
            box-sizing: border-box !important;
          }
        }

        @media (max-width: 520px) {
          .collections-metrics-grid {
            grid-template-columns: 1fr !important;
            gap: 8px !important;
          }
          .collections-filter-dropdowns {
            grid-template-columns: 1fr !important;
          }
          .collection-mobile-card {
            padding: 12px 10px !important;
          }
        }

        @media (min-width: 769px) {
          .collections-mobile-cards-list {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
}

// ============================================================
// STYLES
// ============================================================
const styles = {
  container: {
    fontFamily: "'Inter', sans-serif",
    color: "#1e293b",
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    flexWrap: "wrap",
    gap: "16px",
  },
  titleWrap: {
    display: "flex",
    alignItems: "center",
    gap: "14px",
  },
  titleIconBox: {
    width: "48px",
    height: "48px",
    borderRadius: "14px",
    backgroundColor: "#183B32",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 4px 12px rgba(24, 59, 50, 0.2)",
    flexShrink: 0,
  },
  pageTitle: {
    margin: 0,
    fontSize: "22px",
    fontWeight: "800",
    color: "#183B32",
    letterSpacing: "-0.3px",
  },
  pageSub: {
    margin: "4px 0 0 0",
    fontSize: "13px",
    color: "#64748b",
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  refreshBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    backgroundColor: "#183B32",
    color: "#D4AF37",
    border: "none",
    borderRadius: "10px",
    padding: "10px 16px",
    fontSize: "13px",
    fontWeight: "700",
    cursor: "pointer",
    boxShadow: "0 3px 10px rgba(24, 59, 50, 0.15)",
    transition: "all 0.2s",
  },
  exportBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    backgroundColor: "#ffffff",
    color: "#183B32",
    border: "1.5px solid #dcdcdc",
    borderRadius: "10px",
    padding: "10px 16px",
    fontSize: "13px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.2s",
  },

  // EXPLANATION BANNER
  explanationBanner: {
    background: "linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)",
    border: "1.5px solid #fde68a",
    borderRadius: "16px",
    padding: "18px 20px",
    boxShadow: "0 4px 14px rgba(245, 158, 11, 0.08)",
  },
  explanationHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "10px",
  },
  bannerCloseBtn: {
    background: "none",
    border: "none",
    color: "#92400e",
    cursor: "pointer",
    padding: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  explanationBody: {
    fontSize: "13px",
    color: "#78350f",
    lineHeight: "1.5",
  },
  resolutionPills: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    marginTop: "10px",
  },
  resolutionPill: {
    background: "rgba(255, 255, 255, 0.75)",
    border: "1px solid #fde68a",
    padding: "6px 12px",
    borderRadius: "8px",
    fontSize: "12px",
  },

  // METRICS ROW
  metricsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))",
    gap: "16px",
  },
  metricCard: {
    backgroundColor: "#ffffff",
    borderRadius: "16px",
    padding: "18px 20px",
    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.04)",
    border: "1px solid #e2e8f0",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  metricTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  metricLabel: {
    fontSize: "12px",
    fontWeight: "700",
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  metricIconBox: {
    width: "36px",
    height: "36px",
    borderRadius: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  metricValue: {
    fontSize: "26px",
    fontWeight: "800",
    color: "#183B32",
    fontFamily: "'Playfair Display', serif",
  },
  metricSub: {
    fontSize: "11.5px",
    color: "#64748b",
  },
  actionRequiredBadge: {
    backgroundColor: "#fee2e2",
    color: "#dc2626",
    fontSize: "10px",
    fontWeight: "800",
    padding: "2px 7px",
    borderRadius: "10px",
    letterSpacing: "0.3px",
  },

  // TABS
  tabsContainer: {
    display: "flex",
    gap: "12px",
    borderBottom: "2px solid #e2e8f0",
    paddingBottom: "10px",
    flexWrap: "wrap",
  },
  tabBtn: {
    backgroundColor: "transparent",
    border: "none",
    borderBottom: "3px solid transparent",
    padding: "8px 16px",
    fontSize: "14px",
    fontWeight: "700",
    color: "#64748b",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    transition: "all 0.2s",
  },
  tabBtnActiveRed: {
    color: "#b91c1c",
    borderBottom: "3px solid #dc2626",
  },
  tabBtnActiveGreen: {
    color: "#065f46",
    borderBottom: "3px solid #10b981",
  },
  tabCounter: {
    fontSize: "11px",
    fontWeight: "800",
    padding: "2px 8px",
    borderRadius: "12px",
  },

  // FILTER CARD
  filterCard: {
    backgroundColor: "#ffffff",
    borderRadius: "16px",
    padding: "16px 20px",
    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.04)",
    border: "1px solid #e2e8f0",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "14px",
  },
  searchBox: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    backgroundColor: "#f8fafc",
    border: "1.5px solid #e2e8f0",
    borderRadius: "10px",
    padding: "8px 14px",
    flex: "1",
    minWidth: "260px",
  },
  searchInput: {
    border: "none",
    backgroundColor: "transparent",
    fontSize: "13.5px",
    width: "100%",
    outline: "none",
    color: "#1e293b",
  },
  clearSearchBtn: {
    border: "none",
    background: "none",
    color: "#94a3b8",
    cursor: "pointer",
  },
  filterDropdowns: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    flexWrap: "wrap",
  },
  dropdownWrap: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    backgroundColor: "#f8fafc",
    border: "1.5px solid #e2e8f0",
    borderRadius: "10px",
    padding: "6px 12px",
  },
  select: {
    border: "none",
    backgroundColor: "transparent",
    fontSize: "12.5px",
    fontWeight: "600",
    color: "#334155",
    outline: "none",
    cursor: "pointer",
  },

  // TABLE CARD
  tableCard: {
    backgroundColor: "#ffffff",
    borderRadius: "16px",
    boxShadow: "0 2px 12px rgba(0, 0, 0, 0.04)",
    border: "1px solid #e2e8f0",
    overflow: "hidden",
  },
  tableTopInfo: {
    padding: "14px 20px",
    borderBottom: "1px solid #f1f5f9",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "10px",
    backgroundColor: "#fcfcfd",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    textAlign: "left",
    fontSize: "13px",
  },
  th: {
    padding: "12px 16px",
    backgroundColor: "#f8fafc",
    color: "#475569",
    fontWeight: "700",
    fontSize: "11.5px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    borderBottom: "1.5px solid #e2e8f0",
    whiteSpace: "nowrap",
  },
  tr: {
    borderBottom: "1px solid #f1f5f9",
    transition: "background 0.15s",
  },
  td: {
    padding: "14px 16px",
    verticalAlign: "top",
  },

  // BADGES & TEXTS
  paymentIdBadge: {
    fontFamily: "monospace",
    fontWeight: "700",
    fontSize: "12px",
    color: "#183B32",
    backgroundColor: "#f1f5f9",
    padding: "3px 7px",
    borderRadius: "6px",
    border: "1px solid #e2e8f0",
  },
  copyBtn: {
    border: "none",
    background: "none",
    cursor: "pointer",
    padding: "3px",
    color: "#64748b",
  },
  methodBadge: {
    fontSize: "10.5px",
    fontWeight: "700",
    color: "#183B32",
    backgroundColor: "#fdf8ee",
    border: "1px solid #ebd9a2",
    padding: "2px 6px",
    borderRadius: "6px",
  },
  vpaBadge: {
    fontSize: "10.5px",
    color: "#475569",
    fontFamily: "monospace",
    maxWidth: "140px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  timestampText: {
    fontSize: "11px",
    color: "#94a3b8",
  },
  orderIdSub: {
    fontSize: "10.5px",
    color: "#94a3b8",
    fontFamily: "monospace",
  },

  customerNameText: {
    fontWeight: "700",
    color: "#183B32",
    fontSize: "13.5px",
  },
  phoneLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    color: "#2563eb",
    fontSize: "12px",
    textDecoration: "none",
    fontWeight: "600",
  },
  altPhoneText: {
    fontSize: "11px",
    color: "#64748b",
  },
  emailText: {
    fontSize: "11px",
    color: "#64748b",
    maxWidth: "160px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  addressBlock: {
    display: "flex",
    alignItems: "flex-start",
    gap: "5px",
    color: "#475569",
    maxWidth: "240px",
  },
  addressText: {
    fontSize: "12px",
    lineHeight: "1.4",
  },

  connectedOrderBadge: {
    backgroundColor: "#ecfdf5",
    color: "#065f46",
    border: "1px solid #a7f3d0",
    padding: "3px 8px",
    borderRadius: "6px",
    fontWeight: "700",
    fontSize: "12px",
    display: "inline-block",
  },
  missingWarningBadge: {
    backgroundColor: "#fef2f2",
    color: "#b91c1c",
    border: "1px solid #fecaca",
    padding: "3px 8px",
    borderRadius: "6px",
    fontWeight: "700",
    fontSize: "11px",
    display: "inline-block",
  },
  itemSummaryPill: {
    fontSize: "10.5px",
    color: "#64748b",
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: "4px",
    padding: "2px 5px",
    maxWidth: "180px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  amountText: {
    fontWeight: "800",
    fontSize: "15px",
    color: "#183B32",
    fontFamily: "'Playfair Display', serif",
  },
  refundAmountText: {
    fontSize: "10.5px",
    color: "#dc2626",
    fontWeight: "600",
  },

  capturedStatusBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    backgroundColor: "#ecfdf5",
    color: "#065f46",
    padding: "3px 8px",
    borderRadius: "12px",
    fontSize: "11px",
    fontWeight: "700",
  },
  refundedStatusBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    backgroundColor: "#eff6ff",
    color: "#1d4ed8",
    padding: "3px 8px",
    borderRadius: "12px",
    fontSize: "11px",
    fontWeight: "700",
  },

  actionButtonsCol: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: "6px",
  },
  directRefundBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
    backgroundColor: "#dc2626",
    color: "#ffffff",
    border: "none",
    borderRadius: "8px",
    padding: "6px 12px",
    fontSize: "12px",
    fontWeight: "700",
    cursor: "pointer",
    boxShadow: "0 2px 6px rgba(220, 38, 38, 0.25)",
    transition: "all 0.2s",
    whiteSpace: "nowrap",
  },
  createOrderBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
    backgroundColor: "#183B32",
    color: "#D4AF37",
    border: "none",
    borderRadius: "8px",
    padding: "5px 10px",
    fontSize: "11.5px",
    fontWeight: "700",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  softRefundBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    backgroundColor: "#fff1f2",
    color: "#e11d48",
    border: "1px solid #fecdd3",
    borderRadius: "6px",
    padding: "4px 8px",
    fontSize: "11px",
    fontWeight: "600",
    cursor: "pointer",
  },
  inspectBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    backgroundColor: "#f8fafc",
    color: "#475569",
    border: "1px solid #cbd5e1",
    borderRadius: "6px",
    padding: "4px 8px",
    fontSize: "11px",
    fontWeight: "600",
    cursor: "pointer",
  },
  alreadyRefundedText: {
    fontSize: "11px",
    color: "#94a3b8",
    fontStyle: "italic",
  },

  loadingBox: {
    padding: "60px 20px",
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  emptyBox: {
    padding: "60px 20px",
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  emptyIconCircle: {
    width: "64px",
    height: "64px",
    borderRadius: "50%",
    backgroundColor: "#f8fafc",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "14px",
  },

  // MODALS
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(15, 23, 42, 0.7)",
    backdropFilter: "blur(6px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 99999,
    padding: "16px",
    boxSizing: "border-box",
    overflowY: "auto",
  },
  refundModalBox: {
    backgroundColor: "#ffffff",
    borderRadius: "20px",
    width: "100%",
    maxWidth: "520px",
    boxShadow: "0 25px 60px rgba(0, 0, 0, 0.35)",
    overflow: "hidden",
    border: "2px solid #ef4444",
    animation: "fadeIn 0.2s ease",
  },
  refundModalHeader: {
    background: "linear-gradient(135deg, #b91c1c 0%, #7f1d1d 100%)",
    padding: "24px 24px 20px",
    color: "#ffffff",
    textAlign: "center",
  },
  alertIconCircle: {
    width: "56px",
    height: "56px",
    borderRadius: "50%",
    backgroundColor: "rgba(255, 255, 255, 0.2)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 12px",
    boxShadow: "0 0 0 6px rgba(255, 255, 255, 0.1)",
  },
  refundModalTitle: {
    margin: "0 0 4px 0",
    fontSize: "20px",
    fontWeight: "800",
    letterSpacing: "-0.2px",
  },
  refundModalSub: {
    margin: 0,
    fontSize: "12px",
    color: "#fecaca",
    lineHeight: "1.4",
  },
  refundModalBody: {
    padding: "20px 24px",
    display: "flex",
    flexDirection: "column",
    gap: "14px",
    maxHeight: "65vh",
    overflowY: "auto",
  },
  criticalWarningBox: {
    backgroundColor: "#fef2f2",
    border: "1.5px solid #f87171",
    borderRadius: "12px",
    padding: "12px 14px",
    display: "flex",
    gap: "10px",
    alignItems: "flex-start",
  },
  modalSummaryCard: {
    backgroundColor: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: "12px",
    padding: "12px 16px",
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  modalSummaryRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontSize: "12.5px",
  },
  modalSummaryLabel: {
    color: "#64748b",
    fontWeight: "500",
  },
  modalSummaryValue: {
    fontWeight: "700",
    color: "#1e293b",
  },
  modalSummaryValueMono: {
    fontFamily: "monospace",
    fontWeight: "700",
    color: "#183B32",
    fontSize: "12px",
  },
  modalSummaryValueHighlight: {
    fontWeight: "800",
    color: "#b91c1c",
    fontSize: "14px",
  },
  formGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "5px",
  },
  formLabel: {
    fontSize: "11px",
    fontWeight: "700",
    color: "#475569",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  modalInput: {
    border: "1.5px solid #cbd5e1",
    borderRadius: "10px",
    padding: "10px 14px",
    fontSize: "13.5px",
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
    fontFamily: "inherit",
  },
  confirmationSafetyBox: {
    backgroundColor: "#fff1f2",
    border: "1.5px dashed #f43f5e",
    borderRadius: "12px",
    padding: "12px 14px",
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  confirmPromptInput: {
    border: "2px solid #e11d48",
    borderRadius: "8px",
    padding: "8px 12px",
    fontSize: "14px",
    fontWeight: "800",
    textAlign: "center",
    letterSpacing: "2px",
    outline: "none",
    backgroundColor: "#ffffff",
    color: "#9f1239",
  },
  modalErrorBox: {
    backgroundColor: "#fee2e2",
    color: "#b91c1c",
    border: "1px solid #f87171",
    padding: "10px 14px",
    borderRadius: "8px",
    fontSize: "12.5px",
    fontWeight: "600",
  },
  modalSuccessBox: {
    backgroundColor: "#ecfdf5",
    color: "#065f46",
    border: "1px solid #6ee7b7",
    padding: "10px 14px",
    borderRadius: "8px",
    fontSize: "12.5px",
    fontWeight: "700",
  },
  modalFooter: {
    padding: "14px 24px",
    backgroundColor: "#f8fafc",
    borderTop: "1px solid #e2e8f0",
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
    flexShrink: 0,
  },
  cancelBtn: {
    backgroundColor: "#ffffff",
    color: "#475569",
    border: "1.5px solid #cbd5e1",
    borderRadius: "10px",
    padding: "10px 16px",
    fontSize: "13px",
    fontWeight: "600",
    cursor: "pointer",
  },
  confirmRefundBtn: {
    backgroundColor: "#dc2626",
    color: "#ffffff",
    border: "none",
    borderRadius: "10px",
    padding: "10px 18px",
    fontSize: "13.5px",
    fontWeight: "700",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    boxShadow: "0 4px 12px rgba(220, 38, 38, 0.3)",
  },

  // CREATE ORDER MODAL
  createOrderModalBox: {
    backgroundColor: "#ffffff",
    borderRadius: "20px",
    width: "100%",
    maxWidth: "600px",
    boxShadow: "0 25px 60px rgba(0, 0, 0, 0.35)",
    overflow: "hidden",
    border: "1.5px solid #D4AF37",
  },
  createOrderHeader: {
    backgroundColor: "#183B32",
    padding: "18px 22px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  modalCloseBtn: {
    background: "none",
    border: "none",
    color: "#ffffff",
    cursor: "pointer",
    padding: "4px",
  },
  modalCloseBtnDark: {
    background: "none",
    border: "none",
    color: "#64748b",
    cursor: "pointer",
    padding: "4px",
  },
  createOrderBody: {
    padding: "20px 24px",
    maxHeight: "65vh",
    overflowY: "auto",
  },
  formGrid2: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "14px",
  },
  confirmGoldBtn: {
    backgroundColor: "#183B32",
    color: "#D4AF37",
    border: "none",
    borderRadius: "10px",
    padding: "10px 18px",
    fontSize: "13.5px",
    fontWeight: "700",
    cursor: "pointer",
  },

  // INSPECT MODAL
  inspectModalBox: {
    backgroundColor: "#ffffff",
    borderRadius: "20px",
    width: "100%",
    maxWidth: "700px",
    height: "88vh",
    maxHeight: "850px",
    boxShadow: "0 25px 60px rgba(0, 0, 0, 0.35)",
    overflow: "hidden",
    border: "1px solid #e2e8f0",
    display: "flex",
    flexDirection: "column",
    margin: "auto",
  },
  inspectIconBox: {
    width: "38px",
    height: "38px",
    borderRadius: "10px",
    backgroundColor: "#183B32",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    boxShadow: "0 2px 8px rgba(24, 59, 50, 0.2)",
  },
  inspectHeaderTitle: {
    margin: 0,
    color: "#183B32",
    fontSize: "17px",
    fontWeight: "800",
  },
  inspectHeaderSub: {
    fontSize: "12px",
    color: "#64748b",
    marginTop: "2px",
  },
  inspectHeader: {
    padding: "16px 22px",
    borderBottom: "1px solid #e2e8f0",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: "#f8fafc",
    flexShrink: 0,
  },
  inspectBody: {
    padding: "18px 22px",
    overflowY: "auto",
    overflowX: "hidden",
    flex: "1 1 auto",
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    gap: "14px",
    scrollbarWidth: "thin",
    scrollbarColor: "#94a3b8 #f1f5f9",
  },
  inspectSummaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
    gap: "10px",
    backgroundColor: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: "12px",
    padding: "12px 14px",
    flexShrink: 0,
  },
  inspectSummaryCell: {
    display: "flex",
    flexDirection: "column",
    gap: "3px",
  },
  inspectSummaryLabel: {
    fontSize: "10.5px",
    fontWeight: "700",
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: "0.4px",
  },
  inspectSummaryValueAmount: {
    fontSize: "16px",
    fontWeight: "800",
    color: "#183B32",
  },
  inspectSummaryValueMethod: {
    fontSize: "12px",
    fontWeight: "700",
    color: "#334155",
  },
  inspectCard: {
    backgroundColor: "#ffffff",
    border: "1.5px solid #e2e8f0",
    borderRadius: "14px",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.02)",
    flexShrink: 0,
  },
  inspectCardHeader: {
    backgroundColor: "#f8fafc",
    padding: "10px 16px",
    borderBottom: "1px solid #e2e8f0",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  inspectCardContent: {
    padding: "12px 16px",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  inspectDetailRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontSize: "13px",
    gap: "12px",
  },
  inspectDetailLabel: {
    color: "#64748b",
    fontWeight: "600",
    minWidth: "125px",
    flexShrink: 0,
  },
  inspectDetailVal: {
    color: "#1e293b",
    fontWeight: "500",
    wordBreak: "break-word",
  },
  inspectDetailValBold: {
    color: "#183B32",
    fontWeight: "800",
  },
  inspectAddressText: {
    color: "#1e293b",
    fontWeight: "600",
    lineHeight: "1.45",
    backgroundColor: "#f8fafc",
    padding: "8px 12px",
    borderRadius: "8px",
    borderLeft: "3px solid #183B32",
    flex: 1,
    fontSize: "12.5px",
  },
  inspectOrderStatusPill: {
    backgroundColor: "#ecfdf5",
    color: "#065f46",
    fontSize: "11.5px",
    fontWeight: "700",
    padding: "3px 10px",
    borderRadius: "20px",
    textTransform: "capitalize",
    border: "1px solid #a7f3d0",
  },
  orderedItemsList: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  orderedItemCard: {
    display: "flex",
    alignItems: "center",
    gap: "14px",
    backgroundColor: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: "12px",
    padding: "10px 14px",
    flexShrink: 0,
  },
  orderedItemImg: {
    width: "48px",
    height: "48px",
    borderRadius: "8px",
    objectFit: "cover",
    border: "1px solid #cbd5e1",
    backgroundColor: "#ffffff",
    flexShrink: 0,
  },
  orderedItemImgPlaceholder: {
    width: "48px",
    height: "48px",
    borderRadius: "8px",
    backgroundColor: "#e2e8f0",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  orderedItemName: {
    fontSize: "13.5px",
    fontWeight: "800",
    color: "#183B32",
    marginBottom: "4px",
  },
  orderedItemMetaRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: "6px",
    alignItems: "center",
  },
  orderedItemBadgeId: {
    backgroundColor: "#f1f5f9",
    color: "#475569",
    fontSize: "11px",
    fontWeight: "700",
    padding: "2px 7px",
    borderRadius: "6px",
    border: "1px solid #cbd5e1",
  },
  orderedItemBadgeVariant: {
    backgroundColor: "#eff6ff",
    color: "#1d4ed8",
    fontSize: "11px",
    fontWeight: "600",
    padding: "2px 7px",
    borderRadius: "6px",
    border: "1px solid #bfdbfe",
    textTransform: "capitalize",
  },
  orderedItemPricing: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: "2px",
    flexShrink: 0,
  },
  orderedItemQtyBadge: {
    backgroundColor: "#183B32",
    color: "#ffffff",
    fontSize: "11px",
    fontWeight: "800",
    padding: "2px 8px",
    borderRadius: "10px",
  },
  orderedItemPrice: {
    fontSize: "13.5px",
    fontWeight: "800",
    color: "#183B32",
  },
  orderedItemSubtotal: {
    fontSize: "11px",
    color: "#64748b",
  },
  toggleRawJsonBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    background: "none",
    border: "none",
    color: "#64748b",
    fontSize: "12px",
    fontWeight: "600",
    cursor: "pointer",
    padding: "4px 0",
  },
  rawJsonBox: {
    backgroundColor: "#0f172a",
    borderRadius: "10px",
    padding: "12px",
    marginTop: "8px",
    border: "1px solid #334155",
  },
  rawJsonHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "8px",
    borderBottom: "1px solid #334155",
    paddingBottom: "6px",
  },
  preCode: {
    margin: 0,
    fontSize: "11.5px",
    fontFamily: "monospace",
    color: "#38bdf8",
    overflowX: "auto",
    maxHeight: "220px",
  },
  notesTable: {
    backgroundColor: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: "10px",
    padding: "10px 14px",
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    fontSize: "12px",
  },
  notesRow: {
    display: "flex",
    gap: "8px",
    wordBreak: "break-word",
  },
  notesKey: {
    color: "#183B32",
    minWidth: "120px",
    flexShrink: 0,
  },
  notesVal: {
    color: "#334155",
  },

  // GLOBAL TOAST BANNER
  toastBanner: {
    backgroundColor: "#ffffff",
    border: "2px solid #10b981",
    borderRadius: "14px",
    padding: "16px 20px",
    display: "flex",
    alignItems: "flex-start",
    gap: "14px",
    boxShadow: "0 10px 30px rgba(16, 185, 129, 0.2)",
    animation: "fadeIn 0.25s ease",
    marginBottom: "4px",
  },
  toastIconBox: {
    width: "40px",
    height: "40px",
    borderRadius: "10px",
    backgroundColor: "#ecfdf5",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  toastTitle: {
    fontSize: "15px",
    fontWeight: "800",
    color: "#065f46",
    margin: "0 0 2px 0",
  },
  toastMessage: {
    fontSize: "13px",
    color: "#1e293b",
    lineHeight: "1.4",
  },
  toastMetaRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: "12px",
    marginTop: "6px",
    fontSize: "12px",
    color: "#64748b",
  },
  toastMetaItem: {
    backgroundColor: "#f1f5f9",
    padding: "2px 8px",
    borderRadius: "6px",
  },
  toastCloseBtn: {
    background: "none",
    border: "none",
    color: "#64748b",
    cursor: "pointer",
    padding: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },

  // SUCCESS RECEIPT MODAL VIEW
  successModalBody: {
    padding: "32px 24px 20px",
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    backgroundColor: "#ffffff",
  },
  successIconCircle: {
    width: "70px",
    height: "70px",
    borderRadius: "50%",
    backgroundColor: "#ecfdf5",
    border: "3px solid #a7f3d0",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "16px",
    boxShadow: "0 6px 18px rgba(16, 185, 129, 0.2)",
  },
  successModalTitle: {
    margin: "0 0 6px 0",
    fontSize: "22px",
    fontWeight: "800",
    color: "#065f46",
  },
  successModalSubtitle: {
    margin: "0 0 20px 0",
    fontSize: "13.5px",
    color: "#475569",
    maxWidth: "420px",
    lineHeight: "1.45",
  },
  successReceiptCard: {
    width: "100%",
    backgroundColor: "#f8fafc",
    border: "1.5px solid #e2e8f0",
    borderRadius: "14px",
    padding: "16px 18px",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    marginBottom: "20px",
    boxSizing: "border-box",
    textAlign: "left",
  },
  successCloseBtn: {
    backgroundColor: "#183B32",
    color: "#ffffff",
    border: "none",
    borderRadius: "12px",
    padding: "12px 28px",
    fontSize: "14px",
    fontWeight: "700",
    cursor: "pointer",
    boxShadow: "0 4px 14px rgba(24, 59, 50, 0.2)",
    transition: "all 0.2s",
  },
};
